/**
 * audio/engine.ts — Procedural Audio Engine
 *
 * Hearth has no audio files. Every sound — the fire, the footsteps, the
 * barely-audible room tone — is synthesized in real time from Web Audio
 * oscillators, noise generators, and envelope automation.
 *
 * The goal is not "impressive audio demo." It is: make the room feel
 * inhabited. A fireplace that crackles slightly wrong, a piano chord that
 * decays a little too slowly, a room tone so quiet you only notice when
 * it stops — that is the target. Present without demanding attention.
 *
 * Architecture:
 *   sources → ambientGain → masterGain → reverb → destination
 *   sfx sources → sfxGain → masterGain → reverb → destination
 *
 * AudioContext is only created on the first user gesture (resume()).
 * All methods are safe to call before that — they are no-ops until
 * the context exists.
 */

import type { RoomMood } from "../room/mood.js";
import { getMoodAmbience } from "../room/mood.js";
import {
  createNoise,
  createReverb,
  createLPF,
  createHPF,
  createBPF,
} from "./synth.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AmbientLayer {
  /** The primary source node (oscillator or noise) */
  source: AudioNode;
  /** Dedicated gain for this layer — ramped during crossfades */
  gain: GainNode;
  /** Cleanup function: stops and disconnects all nodes in this layer */
  dispose: () => void;
}

interface FurnitureContext {
  /** Furniture type identifiers present in the current room */
  types: Set<string>;
  /** Per-furniture state (on/off, etc.) */
  states: Map<string, Record<string, unknown>>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CROSSFADE_OUT_DURATION = 2.0; // seconds to fade out a departing layer
const CROSSFADE_IN_DURATION  = 1.0; // seconds to fade in a new layer
const MAX_AMBIENT_NODES      = 20;  // ambient layers budget (room + zone + time-of-day)
const MAX_SFX_NODES          = 16;  // one-shot SFX budget (combat, UI, interactions)

// Pentatonic scale on C4: C D E G A
const PENTATONIC_C4 = [261.63, 293.66, 329.63, 392.00, 440.00];

// ─── AudioEngine ─────────────────────────────────────────────────────────────

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;

  // Shared noise buffers — created once, reused across all noise sources
  private _brownBuffer: AudioBuffer | null = null;
  private _whiteBuffer: AudioBuffer | null = null;
  private _pinkBuffer: AudioBuffer | null = null;

  // Time-of-day and zone/weather state for ambient modulation
  private _timeOfDay: number = 12;
  private _zoneType: string = "residential";
  private _weather: string = "clear";

  // Bird call scheduling (morning ambient)
  private _nextBirdTime: number = 0;
  // Still room swell scheduling
  private _nextSwellTime: number = 0;

  // Active ambient layers keyed by sound identifier
  private activeAmbient: Map<string, AmbientLayer> = new Map();
  // Which sound IDs the current mood wants active
  private currentMoodSounds: string[] = [];

  // Crackle scheduling (fire ambient)
  private nextCrackleTime = 0;

  // Record player melody scheduling
  private nextNoteTime = 0;

  // Fish tank bubble scheduling
  private nextBubbleTime = 0;

  // Separate node budgets — ambient can never starve SFX
  private ambientNodeCount = 0;
  private sfxNodeCount = 0;

  // Guard: title ambient already playing (prevent double-call stacking)
  private _titleAmbientActive = false;

  // Visibility change handling — auto-suspend when hidden, resume when visible
  private _boundVisHandler: (() => void) | null = null;

  // AudioParam dedup — avoid event list accumulation from per-frame setTargetAtTime
  private _lastAmbientVolume = -1;

  // Furniture present in the current room
  private furniture: FurnitureContext = {
    types: new Set(),
    states: new Map(),
  };

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Create or resume the AudioContext.
   *
   * MUST be called from a user gesture (click, keydown, etc.) — the browser
   * will block AudioContext creation otherwise. Safe to call multiple times;
   * subsequent calls just resume a suspended or interrupted context.
   *
   * iOS Safari uses a 4th state "interrupted" when the tab is backgrounded,
   * the screen locks, or a phone call arrives. We must handle this explicitly
   * or audio permanently dies on iOS PWA.
   */
  resume(): void {
    if (!this.ctx) {
      this._init();
    } else if (this.ctx.state === "suspended" || (this.ctx.state as string) === "interrupted") {
      void this.ctx.resume();
    }
  }

  /**
   * Called each frame from the game loop.
   *
   * mood        — current room mood (from computeRoomMood)
   * timeOfDay   — 0-24 float hours
   * dt          — frame delta in seconds
   */
  update(mood: RoomMood, timeOfDay: number, dt: number): void {
    if (!this.ctx || !this.ambientGain) return;
    // Don't schedule anything while the context is suspended/interrupted —
    // automation events would pile up and fire in a burst on resume.
    if (this.ctx.state !== "running") return;
    void dt;

    // Store timeOfDay for use by ambient layers (e.g. night insects gating)
    this._timeOfDay = timeOfDay;

    // ── Derive desired sound set from mood ──────────────────────────────────
    const ambience = getMoodAmbience(mood);
    const desired = new Set(ambience.sounds);

    // Scale the ambient bus by the mood's volume recommendation.
    // Only issue new automation when the target value actually changes —
    // Chrome/Safari do linear scans through AudioParam event lists, so
    // calling setTargetAtTime every frame (60fps) causes O(n) bloat.
    const targetVol = ambience.volume * 0.6;
    if (Math.abs(targetVol - this._lastAmbientVolume) > 0.001) {
      this._lastAmbientVolume = targetVol;
      this.ambientGain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.ambientGain.gain.setTargetAtTime(
        targetVol,
        this.ctx.currentTime,
        0.5
      );
    }

    // ── Stop layers no longer wanted ────────────────────────────────────────
    for (const [id, layer] of this.activeAmbient) {
      if (!desired.has(id)) {
        this._fadeOutLayer(id, layer);
      }
    }

    // ── Start newly-desired layers ───────────────────────────────────────────
    for (const id of desired) {
      if (!this.activeAmbient.has(id)) {
        this._startLayer(id);
      }
    }

    this.currentMoodSounds = [...desired];

    // ── Time-of-day ambient layers ──────────────────────────────────────────
    const isNight = timeOfDay >= 20 || timeOfDay < 5;
    const isMorning = timeOfDay >= 5 && timeOfDay < 9;

    if (isNight) {
      if (!this.activeAmbient.has("amb_night_insects")) {
        this._startLayer("amb_night_insects");
      }
    } else {
      const insectLayer = this.activeAmbient.get("amb_night_insects");
      if (insectLayer) this._fadeOutLayer("amb_night_insects", insectLayer);
    }

    if (isMorning) {
      if (!this.activeAmbient.has("amb_morning_birds")) {
        this._startLayer("amb_morning_birds");
      }
    } else {
      const birdLayer = this.activeAmbient.get("amb_morning_birds");
      if (birdLayer) this._fadeOutLayer("amb_morning_birds", birdLayer);
    }

    // ── Zone-type ambient layers ────────────────────────────────────────────
    const zoneAmbientMap: Record<string, string> = {
      forest: "amb_forest", nature: "amb_forest",
      underground: "amb_cave", cave: "amb_cave",
      harbor: "amb_harbor",
      mountain: "amb_mountain", skyline: "amb_mountain",
      commercial: "amb_market", social: "amb_town",
      danger: "amb_danger",
      training: "amb_training", arena: "amb_training",
      tavern: "amb_tavern", inn: "amb_tavern",
      market: "amb_market", bazaar: "amb_market",
      residential: "amb_residential", housing: "amb_residential",
    };
    const zoneAmbientIds = new Set(Object.values(zoneAmbientMap));
    const desiredZoneAmb = zoneAmbientMap[this._zoneType];

    // Fade out zone layers that don't match the current zone
    for (const [id, layer] of this.activeAmbient) {
      if (zoneAmbientIds.has(id) && id !== desiredZoneAmb) {
        this._fadeOutLayer(id, layer);
      }
    }
    // Start the desired zone ambient if it's not already active
    if (desiredZoneAmb && !this.activeAmbient.has(desiredZoneAmb)) {
      this._startLayer(desiredZoneAmb);
    }

    // ── Weather ambient layers ──────────────────────────────────────────────
    if (this._weather === "rain" || this._weather === "storm") {
      if (!this.activeAmbient.has("amb_rain")) this._startLayer("amb_rain");
      if (!this.activeAmbient.has("amb_wind")) this._startLayer("amb_wind");
    } else if (this._weather === "wind") {
      if (!this.activeAmbient.has("amb_wind")) this._startLayer("amb_wind");
      const rainLayer = this.activeAmbient.get("amb_rain");
      if (rainLayer) this._fadeOutLayer("amb_rain", rainLayer);
    } else {
      const rainLayer = this.activeAmbient.get("amb_rain");
      if (rainLayer) this._fadeOutLayer("amb_rain", rainLayer);
      const windLayer = this.activeAmbient.get("amb_wind");
      if (windLayer) this._fadeOutLayer("amb_wind", windLayer);
    }

    // ── Scheduled events (crackle, notes, bubbles, birds, swells) ───────────
    this._scheduleCrackle();
    this._scheduleRecordNote();
    this._scheduleBubble();
    this._scheduleBirdCall();
    this._scheduleStillSwell();
  }

  /**
   * Notify the engine which furniture is present in the current room.
   * Call this when the room changes (furniture added/removed, state toggled).
   */
  setFurniture(furniture: FurnitureContext): void {
    this.furniture = furniture;
  }

  /**
   * Set the current zone type for zone-aware ambient selection.
   * Zone types: "residential", "forest", "nature", "underground", "cave",
   * "harbor", "mountain", "skyline", "commercial", "social", "danger"
   */
  setZoneType(zoneType: string): void {
    this._zoneType = zoneType;
  }

  /**
   * Set the current weather for weather-aware ambient layers.
   * Weather values: "clear", "rain", "storm", "wind"
   */
  setWeather(weather: string): void {
    this._weather = weather;
  }

  /**
   * Rebuild the room reverb impulse response based on room characteristics.
   * Call when the player enters a new room or the room's material composition
   * changes significantly.
   *
   * @param roomSize  — approximate room area in tiles (e.g. 10×10 = 100)
   * @param material  — dominant wall/floor material: "wood", "stone", "metal", etc.
   */
  setRoomReverb(roomSize: number, material: string): void {
    const ctx = this.ctx;
    if (!ctx || !this.reverb || !this.masterGain) return;

    // Map room size to reverb duration: tiny rooms (16 tiles) → 0.3s, huge (400+) → 1.6s
    const sizeFactor = Math.max(0, Math.min(1, (roomSize - 16) / 384));
    const baseDuration = 0.3 + sizeFactor * 1.3;

    // Material affects decay shape, HF absorption, and tonal character
    let decay = 2.2;
    let lpDecay = 0.4;
    let tonalBias = 0;
    let predelay = 0.004 + sizeFactor * 0.008;

    switch (material) {
      case "stone":
        decay = 1.8;      // slower falloff — stone reverberates
        lpDecay = 0.25;   // low HF absorption — bright reflections
        tonalBias = 0.3;  // bright
        break;
      case "wood":
        decay = 2.5;      // quicker falloff — wood absorbs mid-late
        lpDecay = 0.55;   // moderate HF absorption
        tonalBias = -0.2; // warm
        break;
      case "metal":
        decay = 1.5;      // very ringy
        lpDecay = 0.15;   // very low absorption — bright and hard
        tonalBias = 0.5;  // very bright
        break;
      case "fabric":
        decay = 3.2;      // extremely fast falloff — fabric kills reverb
        lpDecay = 0.85;   // heavy absorption
        tonalBias = -0.4; // very dark
        break;
      case "ceramic":
        decay = 1.9;
        lpDecay = 0.3;
        tonalBias = 0.2;
        break;
      // default (mixed) uses base values
    }

    // Rebuild the convolver — swap out the buffer on the existing node
    const sampleRate = ctx.sampleRate;
    const predelaySamples = Math.floor(sampleRate * predelay);
    const tailLength = Math.max(1, Math.floor(sampleRate * baseDuration));
    const totalLength = predelaySamples + tailLength;
    const impulse = ctx.createBuffer(2, totalLength, sampleRate);

    const effectiveLpDecay = Math.max(0, Math.min(1, lpDecay - tonalBias * 0.3));

    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      const earlyLen = Math.min(Math.floor(sampleRate * 0.02), tailLength);
      for (let i = 0; i < earlyLen; i++) {
        const t = i / tailLength;
        if ((i + ch * 3) % Math.floor(sampleRate * 0.001) < 2) {
          data[predelaySamples + i] = (Math.random() * 2 - 1) * (1 - t * 0.5) * 0.6;
        }
      }
      let lpState = 0;
      for (let i = earlyLen; i < tailLength; i++) {
        const t = i / tailLength;
        const envelope = Math.pow(1 - t, decay);
        const lpCoeff = effectiveLpDecay * t;
        const white = Math.random() * 2 - 1;
        lpState = lpState + lpCoeff * (white - lpState);
        const sample = (white * (1 - lpCoeff) + lpState * lpCoeff);
        data[predelaySamples + i] = sample * envelope;
      }
    }

    this.reverb.buffer = impulse;
  }

  /**
   * Trigger a one-shot footstep sound for the given tile material.
   */
  playFootstep(tileType: string): void {
    if (!this.ctx) return;
    this._playFootstep(tileType);
  }

  /**
   * Trigger a one-shot interaction sound.
   */
  playInteraction(type: string): void {
    if (!this.ctx) return;
    this._playInteraction(type);
  }

  /**
   * Play a material-based impact sound — the physical reality of hitting something.
   * Used for combat against targets of different materials (wood dummy vs metal dummy),
   * furniture placement, and object collisions.
   * @param material — MaterialType from furniture.ts
   * @param intensity — 0-1 intensity (light tap vs full swing)
   */
  playMaterialImpact(material: string, intensity: number = 0.5): void {
    if (!this.ctx) return;
    this._playMaterialImpact(material, intensity);
  }

  /**
   * Play a weapon-class-aware combat hit with target material coloring.
   * This is the physically realistic version of combat_hit — the sound changes
   * based on what you're swinging and what you're hitting.
   *
   * @param weaponClass — "barehand"|"dagger"|"sword_1h"|"sword_2h"|"axe"|"staff"|"mace"|null
   * @param targetMaterial — "organic"|"wood"|"metal"|"stone"|"fabric" (creature body type)
   * @param isCrit — whether this is a critical hit (adds metallic ring)
   * @param intensity — 0-1 hit strength
   */
  playCombatHit(weaponClass: string | null, targetMaterial: string, isCrit: boolean, intensity: number = 0.6): void {
    if (!this.ctx) return;
    (this as any)._playCombatHit(weaponClass ?? "barehand", targetMaterial, isCrit, intensity);
  }

  /**
   * Play a weapon-class-aware swing whoosh. Heavier weapons = deeper, slower.
   * @param weaponClass — weapon type being swung
   */
  playCombatSwing(weaponClass: string | null): void {
    if (!this.ctx) return;
    (this as any)._playCombatSwing(weaponClass ?? "barehand");
  }

  /**
   * Trigger a one-shot creature vocalization.
   * @param type — vocal type: "meow", "bark", "purr", "neigh", "howl", "chirp", "growl", "whinny"
   * @param pitch — frequency multiplier (default 1.0; kittens ~1.3, big cats ~0.7)
   */
  playCreatureVocal(type: string, pitch?: number): void {
    if (!this.ctx) return;
    this._playCreatureVocal(type, pitch ?? 1.0);
  }

  /**
   * Trigger a creature footfall sound appropriate to the species and gait.
   * @param species — creature species: "cat", "dog", "horse", "pony", "wolf", "dragon"
   * @param gait — movement gait: "walk", "trot", "canter", "gallop"
   */
  playCreatureStep(species: string, gait: string): void {
    if (!this.ctx) return;
    this._playCreatureStep(species, gait);
  }

  /**
   * Play the title screen ambient: a warm sustained chord, a gentle fire bed,
   * and scheduled crackles. Called on first user click from the title screen.
   * These layers persist seamlessly into the room — the hearth is already lit.
   */
  playTitleAmbient(): void {
    if (!this.ctx || !this.ambientGain || !this.sfxGain) return;
    if (this._titleAmbientActive) return; // guard against double-click stacking
    this._titleAmbientActive = true;
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // ── Warm pad: Dm chord (D3 + F3 + A3), slow fade in, very gentle ────
    const padFreqs = [146.83, 174.61, 220.00]; // D3, F3, A3
    for (const freq of padFreqs) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;

      // Gentle detune for warmth
      const osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.value = freq * 1.002;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.012, now + 2.5); // 2.5s slow fade in

      osc.connect(gain);
      osc2.connect(gain);
      gain.connect(this.ambientGain);
      osc.start(now);
      osc2.start(now);
      this.ambientNodeCount += 2;

      // Auto-fade out after 6 seconds (room ambient takes over)
      gain.gain.setValueAtTime(0.012, now + 4.0);
      gain.gain.linearRampToValueAtTime(0, now + 8.0);

      const stopTime = now + 8.5;
      osc.stop(stopTime);
      osc2.stop(stopTime);
      osc.addEventListener("ended", () => { osc.disconnect(); this.ambientNodeCount--; });
      osc2.addEventListener("ended", () => { osc2.disconnect(); this.ambientNodeCount--; });
    }

    // ── Fire bed: brown noise through BPF (same as room hearth) ─────────
    if (this.ambientNodeCount < MAX_AMBIENT_NODES) {
      const fireSrc = this._brownSource(ctx);
      const bpf = createBPF(ctx, 800, 2);
      const fireGain = ctx.createGain();
      fireGain.gain.setValueAtTime(0, now);
      fireGain.gain.linearRampToValueAtTime(0.04, now + 1.5);

      fireSrc.connect(bpf);
      bpf.connect(fireGain);
      fireGain.connect(this.ambientGain);
      fireSrc.start(now);
      this.ambientNodeCount++;

      // Store as ambient layer so room transition can take it over
      this.activeAmbient.set("amb_cozy_hearth", {
        source: fireSrc,
        gain: fireGain,
        dispose: () => { fireSrc.stop(); fireSrc.disconnect(); bpf.disconnect(); this.ambientNodeCount--; },
      });
    }

    // ── Seed crackle timer so crackles start immediately ────────────────
    this.nextCrackleTime = now + 0.3;

    // ── Kick a couple of immediate crackles for instant warmth ──────────
    if (this.sfxGain) {
      setTimeout(() => { if (this.ctx && this.sfxGain) this._playCrackle(this.ctx, this.sfxGain); }, 200);
      setTimeout(() => { if (this.ctx && this.sfxGain) this._playCrackle(this.ctx, this.sfxGain); }, 700);
      setTimeout(() => { if (this.ctx && this.sfxGain) this._playCrackle(this.ctx, this.sfxGain); }, 1400);
    }
  }

  /**
   * Lightweight tick for title/transition screens — keeps fire crackles going
   * without needing a room mood. Call from the game loop during non-room scenes
   * when the hearth layer is active.
   */
  tickTitleAudio(): void {
    if (!this.ctx || !this.sfxGain) return;
    if (!this.activeAmbient.has("amb_cozy_hearth")) return;
    this._scheduleCrackle();
  }

  /** Set the master volume (0-1). Ramped smoothly over 100ms. */
  setMasterVolume(v: number): void {
    if (!this.ctx || !this.masterGain) return;
    const clamped = Math.max(0, Math.min(1, v));
    this.masterGain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.033);
  }

  // ─── Initialization ─────────────────────────────────────────────────────────

  private _init(): void {
    try {
      this.ctx = new AudioContext();
    } catch {
      // AudioContext not available — engine stays in no-op mode
      return;
    }

    const ctx = this.ctx;

    // ── Output limiter — prevents clipping when multiple SFX fire simultaneously ──
    // Without this, 3+ combat sounds summing >0dBFS causes audible distortion.
    // -3dB threshold, gentle 4:1 ratio, fast attack for transient protection.
    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -3;
    this.compressor.knee.value = 6;
    this.compressor.ratio.value = 4;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.15;
    this.compressor.connect(ctx.destination);

    // ── Master gain ──────────────────────────────────────────────────────────
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = 0.85;

    // ── Reverb (short, domestic — 0.8s tail) ────────────────────────────────
    this.reverb = createReverb(ctx, 0.8, 2.2);

    // ── Sub-buses ────────────────────────────────────────────────────────────
    this.ambientGain = ctx.createGain();
    this.ambientGain.gain.value = 0.25; // ambient is whisper-quiet — you notice when it stops

    this.sfxGain = ctx.createGain();
    this.sfxGain.gain.value = 1.0;

    // ── Routing: both buses → master → reverb → compressor → output ─────────
    // We mix a small amount of reverb in parallel (not serial) for warmth
    // without muddying transients. Everything goes through the compressor
    // as a brick-wall limiter before hitting the speakers.
    const dryGain = ctx.createGain();
    dryGain.gain.value = 0.82;

    const wetGain = ctx.createGain();
    wetGain.gain.value = 0.18;

    this.ambientGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);

    this.masterGain.connect(dryGain);
    dryGain.connect(this.compressor);

    this.masterGain.connect(this.reverb);
    this.reverb.connect(wetGain);
    wetGain.connect(this.compressor);

    // ── Pre-generate ALL shared noise buffers ────────────────────────────────
    // We generate AudioBufferSourceNodes to steal their buffers, then discard
    // the nodes — reusing the buffers for all subsequent noise sources.
    // All three types up front: brown, white, AND pink. Without pink pre-gen,
    // the first amb_neutral_room layer causes a synchronous buffer fill stutter
    // on mobile devices.
    const brownSrc = createNoise(ctx, "brown");
    const whiteSrc = createNoise(ctx, "white");
    const pinkSrc  = createNoise(ctx, "pink");
    this._brownBuffer = brownSrc.buffer;
    this._whiteBuffer = whiteSrc.buffer;
    this._pinkBuffer  = pinkSrc.buffer;

    // Seed crackle/note/bubble timers
    this.nextCrackleTime = ctx.currentTime + 0.5;
    this.nextNoteTime    = ctx.currentTime + 1.5;
    this.nextBubbleTime  = ctx.currentTime + 2.0;

    // ── iOS Safari "interrupted" state recovery ──────────────────────────────
    // When iOS backgrounds the PWA or a phone call arrives, the context
    // enters "interrupted" state. We listen for statechange to auto-resume
    // when the OS returns control. Safari also suspends on tab switch.
    ctx.addEventListener("statechange", () => {
      if (ctx.state === "running") return;
      // iOS Safari "interrupted" or standard "suspended" — try to resume.
      // This won't always succeed (needs user gesture on some browsers),
      // but catches the case where the OS returns control automatically.
      if (ctx.state === "suspended" || (ctx.state as string) === "interrupted") {
        void ctx.resume();
      }
    });

    // ── Visibility change: suspend when hidden, resume when visible ──────────
    // Browsers throttle setTimeout to 1/sec in hidden tabs. Our scheduled
    // events and fade timers become unreliable. Suspend the context cleanly
    // when hidden and resume when the user returns.
    this._boundVisHandler = () => {
      if (!this.ctx) return;
      if (document.visibilityState === "hidden") {
        // Only suspend if running — don't fight iOS "interrupted" state
        if (this.ctx.state === "running") {
          void this.ctx.suspend();
        }
      } else {
        // Resume on return — handles both "suspended" and iOS "interrupted"
        if (this.ctx.state === "suspended" || (this.ctx.state as string) === "interrupted") {
          void this.ctx.resume();
        }
      }
    };
    document.addEventListener("visibilitychange", this._boundVisHandler);

    void ctx.resume();
  }

  // ─── Ambient Layer Management ────────────────────────────────────────────────

  /**
   * Start an ambient layer and fade it in over CROSSFADE_IN_DURATION seconds.
   */
  private _startLayer(id: string): void {
    const ctx = this.ctx;
    const ambientGain = this.ambientGain;
    if (!ctx || !ambientGain) return;

    // Enforce node count limit — skip non-critical layers if over budget
    if (this.ambientNodeCount >= MAX_AMBIENT_NODES) return;

    const layer = this._buildAmbientLayer(id, ctx);
    if (!layer) return;

    // Fade in
    layer.gain.gain.setValueAtTime(0, ctx.currentTime);
    layer.gain.gain.linearRampToValueAtTime(
      1,
      ctx.currentTime + CROSSFADE_IN_DURATION
    );

    layer.gain.connect(ambientGain);
    this.activeAmbient.set(id, layer);
  }

  /**
   * Fade out an ambient layer and then dispose it.
   *
   * Uses a scheduled silent oscillator to trigger disposal via the "ended"
   * event rather than setTimeout. This is reliable even when the tab is
   * hidden — browsers throttle setTimeout to 1/sec in background tabs, but
   * AudioContext scheduling is authoritative and fires correctly.
   */
  private _fadeOutLayer(id: string, layer: AmbientLayer): void {
    const ctx = this.ctx;
    if (!ctx) return;

    // Remove immediately so update() doesn't try to fade it again
    this.activeAmbient.delete(id);

    const now = ctx.currentTime;
    layer.gain.gain.cancelScheduledValues(now);
    layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
    layer.gain.gain.linearRampToValueAtTime(0, now + CROSSFADE_OUT_DURATION);

    // Schedule disposal using a silent osc "ended" event — works in background tabs
    const timer = ctx.createOscillator();
    const silence = ctx.createGain();
    silence.gain.value = 0;
    timer.connect(silence);
    silence.connect(ctx.destination);
    timer.start(now);
    timer.stop(now + CROSSFADE_OUT_DURATION + 0.1);
    timer.addEventListener("ended", () => {
      timer.disconnect();
      silence.disconnect();
      layer.dispose();
    });
  }

  /**
   * Build the synthesis chain for a named ambient layer.
   * Returns null if the layer is unrecognized or can't be built.
   */
  private _buildAmbientLayer(id: string, ctx: AudioContext): AmbientLayer | null {
    switch (id) {

      // ── Room tone: the beautiful feeling of being inside ─────────────────────
      // Not a chord. Not a drone. The sensation of enclosed air, wood settling,
      // distant world outside, and the faintest hum of a building breathing.
      case "amb_neutral_room": {
        const gain = ctx.createGain();
        gain.gain.value = 1;
        const nodes: (OscillatorNode | AudioBufferSourceNode)[] = [];

        // Layer 1: Room air — very soft pink noise through tight LPF
        // The sound of air in a room with walls. Barely there.
        const roomAir = this._pinkSource(ctx);
        const roomAirLPF = createLPF(ctx, 300, 0.7);
        const roomAirGain = ctx.createGain();
        roomAirGain.gain.value = 0.003;
        roomAir.connect(roomAirLPF);
        roomAirLPF.connect(roomAirGain);
        roomAirGain.connect(gain);
        roomAir.start();
        nodes.push(roomAir);

        // Layer 2: Building resonance — 55Hz sub sine, the body of the structure
        // You don't hear it. You feel it. Like standing inside a cathedral.
        const buildingSub = ctx.createOscillator();
        buildingSub.type = "sine";
        buildingSub.frequency.value = 55;
        const subGain = ctx.createGain();
        subGain.gain.value = 0.003;
        buildingSub.connect(subGain);
        subGain.connect(gain);
        buildingSub.start();
        nodes.push(buildingSub);

        // Layer 3: Outside wind pressure — distant world bleeding through walls
        // Brown noise through very tight LPF with slow modulation
        const windBed = this._brownSource(ctx);
        const windLPF = createLPF(ctx, 120, 0.5);
        const windGain = ctx.createGain();
        windGain.gain.value = 0.002;
        // Slow modulation: 0.03Hz (33-second cycle) — wind comes and goes
        const windLFO = ctx.createOscillator();
        windLFO.type = "sine";
        windLFO.frequency.value = 0.03;
        const windLFODepth = ctx.createGain();
        windLFODepth.gain.value = 0.001;
        windLFO.connect(windLFODepth);
        windLFODepth.connect(windGain.gain);
        windBed.connect(windLPF);
        windLPF.connect(windGain);
        windGain.connect(gain);
        windBed.start();
        windLFO.start();
        nodes.push(windBed, windLFO);

        // Layer 4: Warm harmonic shimmer — two detuned sines at 110Hz (A2)
        // The faintest hum, like old floorboards remembering sunlight
        const warmA = ctx.createOscillator();
        warmA.type = "sine";
        warmA.frequency.value = 110;
        const warmB = ctx.createOscillator();
        warmB.type = "sine";
        warmB.frequency.value = 110;
        warmB.detune.value = 3;
        const warmGain = ctx.createGain();
        warmGain.gain.value = 0.002;
        warmA.connect(warmGain);
        warmB.connect(warmGain);
        warmGain.connect(gain);
        warmA.start();
        warmB.start();
        nodes.push(warmA, warmB);

        // 1 pink + 1 sub + 1 brown + 1 windLFO + 2 warm = 6 nodes
        this.ambientNodeCount += 6;
        return {
          source: nodes[0] as AudioBufferSourceNode, gain,
          dispose: () => {
            for (const n of nodes) { n.stop(); n.disconnect(); }
            roomAirLPF.disconnect(); roomAirGain.disconnect();
            subGain.disconnect();
            windLPF.disconnect(); windGain.disconnect(); windLFODepth.disconnect();
            warmGain.disconnect();
            this.ambientNodeCount -= 6;
          },
        };
      }

      // ── Cozy hearth: warm crackling noise bed + harmonic fire tones ─────────
      case "amb_cozy_hearth":
      case "sfx_fire_crackle": {
        // Noise bed: brown noise through BPF 600Hz Q=1.5 — crackling warmth
        const source = this._brownSource(ctx);
        const bpf = createBPF(ctx, 600, 1.5);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.005;
        source.connect(bpf);
        bpf.connect(noiseGain);

        // Harmonic warmth: A3 (220Hz) + E4 (330Hz) with slow tremolo
        const oscA = ctx.createOscillator();
        oscA.type = "sine";
        oscA.frequency.value = 220;
        const oscE = ctx.createOscillator();
        oscE.type = "sine";
        oscE.frequency.value = 330;

        const tremoloLFO = ctx.createOscillator();
        tremoloLFO.type = "sine";
        tremoloLFO.frequency.value = 0.08;
        const tremoloDepth = ctx.createGain();
        tremoloDepth.gain.value = 0.002;

        const toneGainA = ctx.createGain();
        toneGainA.gain.value = 0.003;
        const toneGainE = ctx.createGain();
        toneGainE.gain.value = 0.003;

        tremoloLFO.connect(tremoloDepth);
        tremoloDepth.connect(toneGainA.gain);
        tremoloDepth.connect(toneGainE.gain);

        oscA.connect(toneGainA);
        oscE.connect(toneGainE);

        // Sum into a single output gain
        const gain = ctx.createGain();
        gain.gain.value = 1;
        noiseGain.connect(gain);
        toneGainA.connect(gain);
        toneGainE.connect(gain);

        source.start();
        oscA.start();
        oscE.start();
        tremoloLFO.start();
        // 1 noise + 2 oscillators + 1 LFO = 4 nodes
        this.ambientNodeCount += 4;

        return {
          source, gain,
          dispose: () => {
            source.stop(); source.disconnect(); bpf.disconnect(); noiseGain.disconnect();
            oscA.stop(); oscA.disconnect(); toneGainA.disconnect();
            oscE.stop(); oscE.disconnect(); toneGainE.disconnect();
            tremoloLFO.stop(); tremoloLFO.disconnect(); tremoloDepth.disconnect();
            this.ambientNodeCount -= 4;
          },
        };
      }

      // ── Mystery drone: a 55Hz sine with slow tremolo ──────────────────────
      case "amb_mystery_drone": {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 55;

        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.1; // very slow — 10 seconds per cycle

        const lfoGain = ctx.createGain();
        lfoGain.gain.value = 0.4; // LFO depth

        const gain = ctx.createGain();
        gain.gain.value = 0.015;

        lfo.connect(lfoGain);
        lfoGain.connect(gain.gain); // LFO modulates the gain
        osc.connect(gain);
        osc.start();
        lfo.start();
        this.ambientNodeCount += 2;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); lfo.stop();
            osc.disconnect(); lfo.disconnect(); lfoGain.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Soft hum: gentle mid drone, less mysterious than the 55Hz variant ──
      case "amb_soft_hum": {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 110;
        const gain = ctx.createGain();
        gain.gain.value = 0.008;
        osc.connect(gain);
        osc.start();
        this.ambientNodeCount++;
        return {
          source: osc, gain,
          dispose: () => { osc.stop(); osc.disconnect(); this.ambientNodeCount--; },
        };
      }

      // ── Still room: near-silence — sub-bass hum + barely-there pink noise ──
      case "amb_still_room": {
        // Sub-bass: C2 (65.41Hz) — you feel it more than hear it
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 65.41;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.004;
        osc.connect(oscGain);

        // Pink noise floor through LPF 100Hz — the sound of quiet
        const pinkSrc = this._pinkSource(ctx);
        const lpf = createLPF(ctx, 100, 0.7);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.003;
        pinkSrc.connect(lpf);
        lpf.connect(noiseGain);

        // Sum into output gain
        const gain = ctx.createGain();
        gain.gain.value = 1;
        oscGain.connect(gain);
        noiseGain.connect(gain);

        osc.start();
        pinkSrc.start();
        this.ambientNodeCount += 2;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); osc.disconnect(); oscGain.disconnect();
            pinkSrc.stop(); pinkSrc.disconnect(); lpf.disconnect(); noiseGain.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Active room: alive, not mechanical — pink noise + gentle beating pair ─
      case "amb_active_room": {
        // Pink noise through BPF 400Hz Q=0.8 — broad, present, breathing
        const source = this._pinkSource(ctx);
        const bpf = createBPF(ctx, 400, 0.8);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.01;
        source.connect(bpf);
        bpf.connect(noiseGain);

        // Detuned G3 pair for gentle beating (196Hz vs 196.4Hz)
        const osc1 = ctx.createOscillator();
        osc1.type = "sine";
        osc1.frequency.value = 196;
        const osc1Gain = ctx.createGain();
        osc1Gain.gain.value = 0.005;
        osc1.connect(osc1Gain);

        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.value = 196.4;
        const osc2Gain = ctx.createGain();
        osc2Gain.gain.value = 0.005;
        osc2.connect(osc2Gain);

        // Sum into output gain
        const gain = ctx.createGain();
        gain.gain.value = 1;
        noiseGain.connect(gain);
        osc1Gain.connect(gain);
        osc2Gain.connect(gain);

        source.start();
        osc1.start();
        osc2.start();
        // 1 noise + 2 oscillators = 3 nodes
        this.ambientNodeCount += 3;

        return {
          source, gain,
          dispose: () => {
            source.stop(); source.disconnect(); bpf.disconnect(); noiseGain.disconnect();
            osc1.stop(); osc1.disconnect(); osc1Gain.disconnect();
            osc2.stop(); osc2.disconnect(); osc2Gain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Record player: warm vinyl pentatonic melody ──────────────────────
      // The actual notes are scheduled in _scheduleRecordNote().
      // This layer just sets up the bus and a very soft noise bed.
      case "music_record_player": {
        const source = this._brownSource(ctx);
        const lpf = createLPF(ctx, 1000, 0.7);
        const gain = ctx.createGain();
        gain.gain.value = 0.008; // almost inaudible noise bed (vinyl hiss)
        source.connect(lpf);
        lpf.connect(gain);
        source.start();
        this.ambientNodeCount++;
        this.nextNoteTime = ctx.currentTime + 0.5;
        return {
          source, gain,
          dispose: () => { source.stop(); source.disconnect(); lpf.disconnect(); this.ambientNodeCount--; },
        };
      }

      // ── Gentle chatter / crowd murmur: band-passed noise swell ──────────
      case "amb_gentle_chatter": {
        const source = this._brownSource(ctx);
        const bpf = createBPF(ctx, 600, 1.2);
        const gain = ctx.createGain();
        gain.gain.value = 0.018;
        source.connect(bpf);
        bpf.connect(gain);
        source.start();
        this.ambientNodeCount++;
        return {
          source, gain,
          dispose: () => { source.stop(); source.disconnect(); bpf.disconnect(); this.ambientNodeCount--; },
        };
      }

      case "amb_crowd_murmur": {
        const source = this._brownSource(ctx);
        const bpf = createBPF(ctx, 800, 0.8);
        const gain = ctx.createGain();
        gain.gain.value = 0.03;
        source.connect(bpf);
        bpf.connect(gain);
        source.start();
        this.ambientNodeCount++;
        return {
          source, gain,
          dispose: () => { source.stop(); source.disconnect(); bpf.disconnect(); this.ambientNodeCount--; },
        };
      }

      // ── Garden ambience: high-passed noise for leaves/air ───────────────
      case "amb_garden":
      case "sfx_leaves": {
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 2000, 0.7);
        const lpf = createLPF(ctx, 8000, 0.7);
        const gain = ctx.createGain();
        gain.gain.value = id === "amb_garden" ? 0.025 : 0.015;
        source.connect(hpf);
        hpf.connect(lpf);
        lpf.connect(gain);
        source.start();
        this.ambientNodeCount++;
        return {
          source, gain,
          dispose: () => {
            source.stop(); source.disconnect();
            hpf.disconnect(); lpf.disconnect();
            this.ambientNodeCount--;
          },
        };
      }

      // ── Forest: layered nature with wind-through-trees variation ──────────
      case "amb_forest": {
        // Leaf rustle: pink noise through HPF 3000Hz → LPF 8000Hz
        const pinkSrc = this._pinkSource(ctx);
        const hpf = createHPF(ctx, 3000, 0.7);
        const lpf = createLPF(ctx, 8000, 0.7);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.015;
        pinkSrc.connect(hpf);
        hpf.connect(lpf);
        lpf.connect(noiseGain);

        // Slow LFO (0.15Hz) modulates the HPF cutoff between 2000-4000Hz
        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.15;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = 1000; // ±1000Hz around the 3000Hz center
        lfo.connect(lfoDepth);
        lfoDepth.connect(hpf.frequency);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        noiseGain.connect(gain);

        pinkSrc.start();
        lfo.start();
        this.ambientNodeCount += 2;

        return {
          source: pinkSrc, gain,
          dispose: () => {
            pinkSrc.stop(); pinkSrc.disconnect(); hpf.disconnect(); lpf.disconnect(); noiseGain.disconnect();
            lfo.stop(); lfo.disconnect(); lfoDepth.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Cave: deep drone + dripping resonance ─────────────────────────────
      case "amb_cave": {
        // Deep drone: sine at 55Hz with very slow tremolo
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 55;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.008;

        const tremoloLFO = ctx.createOscillator();
        tremoloLFO.type = "sine";
        tremoloLFO.frequency.value = 0.05;
        const tremoloDepth = ctx.createGain();
        tremoloDepth.gain.value = 0.003;
        tremoloLFO.connect(tremoloDepth);
        tremoloDepth.connect(oscGain.gain);

        osc.connect(oscGain);

        // Dripping resonance: brown noise through BPF 200Hz Q=3
        const brownSrc = this._brownSource(ctx);
        const bpf = createBPF(ctx, 200, 3);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.006;
        brownSrc.connect(bpf);
        bpf.connect(noiseGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        oscGain.connect(gain);
        noiseGain.connect(gain);

        osc.start();
        tremoloLFO.start();
        brownSrc.start();
        this.ambientNodeCount += 3;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); osc.disconnect(); oscGain.disconnect();
            tremoloLFO.stop(); tremoloLFO.disconnect(); tremoloDepth.disconnect();
            brownSrc.stop(); brownSrc.disconnect(); bpf.disconnect(); noiseGain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Harbor: rhythmic water + seagull-like air ─────────────────────────
      case "amb_harbor": {
        // Water: brown noise through LPF 400Hz with 0.2Hz gain LFO (wave rhythm)
        const brownSrc = this._brownSource(ctx);
        const lpf = createLPF(ctx, 400, 0.7);
        const waterGain = ctx.createGain();
        waterGain.gain.value = 0.012;

        const waveLFO = ctx.createOscillator();
        waveLFO.type = "sine";
        waveLFO.frequency.value = 0.2;
        const waveDepth = ctx.createGain();
        waveDepth.gain.value = 0.006;
        waveLFO.connect(waveDepth);
        waveDepth.connect(waterGain.gain);

        brownSrc.connect(lpf);
        lpf.connect(waterGain);

        // Air: pink noise HPF 4000Hz — distant, airy
        const pinkSrc = this._pinkSource(ctx);
        const hpf = createHPF(ctx, 4000, 0.7);
        const airGain = ctx.createGain();
        airGain.gain.value = 0.004;
        pinkSrc.connect(hpf);
        hpf.connect(airGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        waterGain.connect(gain);
        airGain.connect(gain);

        brownSrc.start();
        waveLFO.start();
        pinkSrc.start();
        this.ambientNodeCount += 3;

        return {
          source: brownSrc, gain,
          dispose: () => {
            brownSrc.stop(); brownSrc.disconnect(); lpf.disconnect(); waterGain.disconnect();
            waveLFO.stop(); waveLFO.disconnect(); waveDepth.disconnect();
            pinkSrc.stop(); pinkSrc.disconnect(); hpf.disconnect(); airGain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Mountain: wind + vast open resonance ──────────────────────────────
      case "amb_mountain": {
        // Wind: white noise through BPF 1200Hz Q=0.5, LFO modulating frequency
        const whiteSrc = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 1200, 0.5);
        const windGain = ctx.createGain();
        windGain.gain.value = 0.015;

        const windLFO = ctx.createOscillator();
        windLFO.type = "sine";
        windLFO.frequency.value = 0.1;
        const windLFODepth = ctx.createGain();
        windLFODepth.gain.value = 400; // ±400Hz around 1200Hz center (800-1600)
        windLFO.connect(windLFODepth);
        windLFODepth.connect(bpf.frequency);

        whiteSrc.connect(bpf);
        bpf.connect(windGain);

        // Vast resonance: sine pad at 110Hz
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 110;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.004;
        osc.connect(oscGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        windGain.connect(gain);
        oscGain.connect(gain);

        whiteSrc.start();
        windLFO.start();
        osc.start();
        this.ambientNodeCount += 3;

        return {
          source: whiteSrc, gain,
          dispose: () => {
            whiteSrc.stop(); whiteSrc.disconnect(); bpf.disconnect(); windGain.disconnect();
            windLFO.stop(); windLFO.disconnect(); windLFODepth.disconnect();
            osc.stop(); osc.disconnect(); oscGain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Town: distant urban murmur + ambient air ──────────────────────────
      case "amb_town": {
        // Murmur base: brown noise through BPF 500Hz Q=1.2
        const brownSrc = this._brownSource(ctx);
        const bpf = createBPF(ctx, 500, 1.2);
        const murmurGain = ctx.createGain();
        murmurGain.gain.value = 0.01;
        brownSrc.connect(bpf);
        bpf.connect(murmurGain);

        // Ambient air: pink noise HPF 2000Hz LPF 6000Hz
        const pinkSrc = this._pinkSource(ctx);
        const hpf = createHPF(ctx, 2000, 0.7);
        const lpf = createLPF(ctx, 6000, 0.7);
        const airGain = ctx.createGain();
        airGain.gain.value = 0.005;
        pinkSrc.connect(hpf);
        hpf.connect(lpf);
        lpf.connect(airGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        murmurGain.connect(gain);
        airGain.connect(gain);

        brownSrc.start();
        pinkSrc.start();
        this.ambientNodeCount += 2;

        return {
          source: brownSrc, gain,
          dispose: () => {
            brownSrc.stop(); brownSrc.disconnect(); bpf.disconnect(); murmurGain.disconnect();
            pinkSrc.stop(); pinkSrc.disconnect(); hpf.disconnect(); lpf.disconnect(); airGain.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Underground: deep rumble + ominous subsonic ───────────────────────
      case "amb_underground": {
        // Deep rumble: brown noise LPF 150Hz
        const brownSrc = this._brownSource(ctx);
        const lpf = createLPF(ctx, 150, 0.7);
        const rumbleGain = ctx.createGain();
        rumbleGain.gain.value = 0.008;
        brownSrc.connect(lpf);
        lpf.connect(rumbleGain);

        // Ominous subsonic: sine at 40Hz with 0.03Hz LFO
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 40;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.006;

        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.03;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = 0.004;
        lfo.connect(lfoDepth);
        lfoDepth.connect(oscGain.gain);

        osc.connect(oscGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        rumbleGain.connect(gain);
        oscGain.connect(gain);

        brownSrc.start();
        osc.start();
        lfo.start();
        this.ambientNodeCount += 3;

        return {
          source: brownSrc, gain,
          dispose: () => {
            brownSrc.stop(); brownSrc.disconnect(); lpf.disconnect(); rumbleGain.disconnect();
            osc.stop(); osc.disconnect(); oscGain.disconnect();
            lfo.stop(); lfo.disconnect(); lfoDepth.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Danger: dissonant tension ─────────────────────────────────────────
      case "amb_danger": {
        // Triangle wave at 55Hz with 0.2Hz tremolo
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.value = 55;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.004;

        const tremoloLFO = ctx.createOscillator();
        tremoloLFO.type = "sine";
        tremoloLFO.frequency.value = 0.2;
        const tremoloDepth = ctx.createGain();
        tremoloDepth.gain.value = 0.003;
        tremoloLFO.connect(tremoloDepth);
        tremoloDepth.connect(oscGain.gain);

        osc.connect(oscGain);

        // Brown noise BPF 300Hz Q=2 — uneasy presence
        const brownSrc = this._brownSource(ctx);
        const bpf = createBPF(ctx, 300, 2);
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.004;
        brownSrc.connect(bpf);
        bpf.connect(noiseGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        oscGain.connect(gain);
        noiseGain.connect(gain);

        osc.start();
        tremoloLFO.start();
        brownSrc.start();
        this.ambientNodeCount += 3;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); osc.disconnect(); oscGain.disconnect();
            tremoloLFO.stop(); tremoloLFO.disconnect(); tremoloDepth.disconnect();
            brownSrc.stop(); brownSrc.disconnect(); bpf.disconnect(); noiseGain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Training grounds: distant metal impacts + exertion ────────────────
      case "amb_training": {
        // Metal clanging: white noise through tight BPF at 2000Hz Q=4
        const clangSrc = this._whiteSource(ctx);
        const clangBPF = createBPF(ctx, 2000, 4);
        const clangGain = ctx.createGain();
        clangGain.gain.value = 0.003;
        // Rhythmic modulation: 1.5Hz — the pace of practice swings
        const clangLFO = ctx.createOscillator();
        clangLFO.type = "sine";
        clangLFO.frequency.value = 1.5;
        const clangLFODepth = ctx.createGain();
        clangLFODepth.gain.value = 0.002;
        clangLFO.connect(clangLFODepth);
        clangLFODepth.connect(clangGain.gain);
        clangSrc.connect(clangBPF);
        clangBPF.connect(clangGain);

        // Exertion murmur: brown noise BPF 400Hz — distant grunts, shuffling feet
        const exertSrc = this._brownSource(ctx);
        const exertBPF = createBPF(ctx, 400, 1.5);
        const exertGain = ctx.createGain();
        exertGain.gain.value = 0.002;
        exertSrc.connect(exertBPF);
        exertBPF.connect(exertGain);

        // Dusty open air: pink noise HPF 3000Hz — outdoor space
        const airSrc = this._pinkSource(ctx);
        const airHPF = createHPF(ctx, 3000, 0.7);
        const airGain = ctx.createGain();
        airGain.gain.value = 0.001;
        airSrc.connect(airHPF);
        airHPF.connect(airGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        clangGain.connect(gain);
        exertGain.connect(gain);
        airGain.connect(gain);

        clangSrc.start(); clangLFO.start(); exertSrc.start(); airSrc.start();
        this.ambientNodeCount += 4;
        return {
          source: clangSrc, gain,
          dispose: () => {
            clangSrc.stop(); clangSrc.disconnect(); clangBPF.disconnect(); clangGain.disconnect();
            clangLFO.stop(); clangLFO.disconnect(); clangLFODepth.disconnect();
            exertSrc.stop(); exertSrc.disconnect(); exertBPF.disconnect(); exertGain.disconnect();
            airSrc.stop(); airSrc.disconnect(); airHPF.disconnect(); airGain.disconnect();
            this.ambientNodeCount -= 4;
          },
        };
      }

      // ── Tavern: warm murmur + clink + hearth fire ─────────────────────────
      case "amb_tavern": {
        // Warm conversation: brown noise BPF 700Hz — rich, convivial voices
        const voiceSrc = this._brownSource(ctx);
        const voiceBPF = createBPF(ctx, 700, 1.0);
        const voiceGain = ctx.createGain();
        voiceGain.gain.value = 0.008;
        voiceSrc.connect(voiceBPF);
        voiceBPF.connect(voiceGain);

        // Clink/glassware: white noise through tight BPF 3500Hz — high sparkle
        const clinkSrc = this._whiteSource(ctx);
        const clinkBPF = createBPF(ctx, 3500, 6);
        const clinkGain = ctx.createGain();
        clinkGain.gain.value = 0.001;
        // Irregular rhythm via slow modulation
        const clinkLFO = ctx.createOscillator();
        clinkLFO.type = "sine";
        clinkLFO.frequency.value = 0.7;
        const clinkDepth = ctx.createGain();
        clinkDepth.gain.value = 0.001;
        clinkLFO.connect(clinkDepth);
        clinkDepth.connect(clinkGain.gain);
        clinkSrc.connect(clinkBPF);
        clinkBPF.connect(clinkGain);

        // Fire warmth: brown noise LPF 500Hz — the hearth
        const fireSrc = this._brownSource(ctx);
        const fireLPF = createLPF(ctx, 500, 1.2);
        const fireGain = ctx.createGain();
        fireGain.gain.value = 0.003;
        fireSrc.connect(fireLPF);
        fireLPF.connect(fireGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        voiceGain.connect(gain);
        clinkGain.connect(gain);
        fireGain.connect(gain);

        voiceSrc.start(); clinkSrc.start(); clinkLFO.start(); fireSrc.start();
        this.ambientNodeCount += 4;
        return {
          source: voiceSrc, gain,
          dispose: () => {
            voiceSrc.stop(); voiceSrc.disconnect(); voiceBPF.disconnect(); voiceGain.disconnect();
            clinkSrc.stop(); clinkSrc.disconnect(); clinkBPF.disconnect(); clinkGain.disconnect();
            clinkLFO.stop(); clinkLFO.disconnect(); clinkDepth.disconnect();
            fireSrc.stop(); fireSrc.disconnect(); fireLPF.disconnect(); fireGain.disconnect();
            this.ambientNodeCount -= 4;
          },
        };
      }

      // ── Market: commercial bustle + coins + vendor calls ──────────────────
      case "amb_market": {
        // Crowd: brown noise BPF 600Hz — broader and louder than tavern
        const crowdSrc = this._brownSource(ctx);
        const crowdBPF = createBPF(ctx, 600, 0.8);
        const crowdGain = ctx.createGain();
        crowdGain.gain.value = 0.01;
        crowdSrc.connect(crowdBPF);
        crowdBPF.connect(crowdGain);

        // Coins/metal: white noise tight BPF 4500Hz — the jingle of commerce
        const coinSrc = this._whiteSource(ctx);
        const coinBPF = createBPF(ctx, 4500, 5);
        const coinGain = ctx.createGain();
        coinGain.gain.value = 0.002;
        coinSrc.connect(coinBPF);
        coinBPF.connect(coinGain);

        // Footsteps: pink noise BPF 800Hz — shuffling crowd
        const stepSrc = this._pinkSource(ctx);
        const stepBPF = createBPF(ctx, 800, 2);
        const stepGain = ctx.createGain();
        stepGain.gain.value = 0.003;
        stepSrc.connect(stepBPF);
        stepBPF.connect(stepGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        crowdGain.connect(gain);
        coinGain.connect(gain);
        stepGain.connect(gain);

        crowdSrc.start(); coinSrc.start(); stepSrc.start();
        this.ambientNodeCount += 3;
        return {
          source: crowdSrc, gain,
          dispose: () => {
            crowdSrc.stop(); crowdSrc.disconnect(); crowdBPF.disconnect(); crowdGain.disconnect();
            coinSrc.stop(); coinSrc.disconnect(); coinBPF.disconnect(); coinGain.disconnect();
            stepSrc.stop(); stepSrc.disconnect(); stepBPF.disconnect(); stepGain.disconnect();
            this.ambientNodeCount -= 3;
          },
        };
      }

      // ── Residential: quiet interior with distant life ──────────────────────
      case "amb_residential": {
        // Indoor air: pink noise through tight LPF 250Hz — muffled walls
        const airSrc = this._pinkSource(ctx);
        const airLPF = createLPF(ctx, 250, 0.7);
        const airGain = ctx.createGain();
        airGain.gain.value = 0.002;
        airSrc.connect(airLPF);
        airLPF.connect(airGain);

        // Distant life: brown noise BPF 350Hz — neighbors, footsteps above
        const lifeSrc = this._brownSource(ctx);
        const lifeBPF = createBPF(ctx, 350, 1.5);
        const lifeGain = ctx.createGain();
        lifeGain.gain.value = 0.002;
        // Very slow swell: 0.04Hz — life comes and goes
        const lifeLFO = ctx.createOscillator();
        lifeLFO.type = "sine";
        lifeLFO.frequency.value = 0.04;
        const lifeLFODepth = ctx.createGain();
        lifeLFODepth.gain.value = 0.001;
        lifeLFO.connect(lifeLFODepth);
        lifeLFODepth.connect(lifeGain.gain);
        lifeSrc.connect(lifeBPF);
        lifeBPF.connect(lifeGain);

        // Building warmth: 82Hz sine (low E) — the building itself
        const buildOsc = ctx.createOscillator();
        buildOsc.type = "sine";
        buildOsc.frequency.value = 82;
        const buildGain = ctx.createGain();
        buildGain.gain.value = 0.002;
        buildOsc.connect(buildGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        airGain.connect(gain);
        lifeGain.connect(gain);
        buildGain.connect(gain);

        airSrc.start(); lifeSrc.start(); lifeLFO.start(); buildOsc.start();
        this.ambientNodeCount += 4;
        return {
          source: airSrc, gain,
          dispose: () => {
            airSrc.stop(); airSrc.disconnect(); airLPF.disconnect(); airGain.disconnect();
            lifeSrc.stop(); lifeSrc.disconnect(); lifeBPF.disconnect(); lifeGain.disconnect();
            lifeLFO.stop(); lifeLFO.disconnect(); lifeLFODepth.disconnect();
            buildOsc.stop(); buildOsc.disconnect(); buildGain.disconnect();
            this.ambientNodeCount -= 4;
          },
        };
      }

      // ── Rain: steady rainfall + sparkle on windows ────────────────────────
      case "amb_rain": {
        // Rainfall: pink noise through LPF 6000Hz
        const pinkSrc = this._pinkSource(ctx);
        const lpf = createLPF(ctx, 6000, 0.7);
        const rainGain = ctx.createGain();
        rainGain.gain.value = 0.012;
        pinkSrc.connect(lpf);
        lpf.connect(rainGain);

        // Roof sparkle: white noise HPF 8000Hz
        const whiteSrc = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 8000, 0.7);
        const sparkleGain = ctx.createGain();
        sparkleGain.gain.value = 0.004;
        whiteSrc.connect(hpf);
        hpf.connect(sparkleGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        rainGain.connect(gain);
        sparkleGain.connect(gain);

        pinkSrc.start();
        whiteSrc.start();
        this.ambientNodeCount += 2;

        return {
          source: pinkSrc, gain,
          dispose: () => {
            pinkSrc.stop(); pinkSrc.disconnect(); lpf.disconnect(); rainGain.disconnect();
            whiteSrc.stop(); whiteSrc.disconnect(); hpf.disconnect(); sparkleGain.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Wind: modulated broadband gusts ───────────────────────────────────
      case "amb_wind": {
        const whiteSrc = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 800, 0.6);
        const windGain = ctx.createGain();
        windGain.gain.value = 0.006;

        // 0.08Hz LFO modulating BPF frequency between 500-1200Hz
        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 0.08;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = 350; // ±350Hz around 800Hz center
        lfo.connect(lfoDepth);
        lfoDepth.connect(bpf.frequency);

        whiteSrc.connect(bpf);
        bpf.connect(windGain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        windGain.connect(gain);

        whiteSrc.start();
        lfo.start();
        this.ambientNodeCount += 2;

        return {
          source: whiteSrc, gain,
          dispose: () => {
            whiteSrc.stop(); whiteSrc.disconnect(); bpf.disconnect(); windGain.disconnect();
            lfo.stop(); lfo.disconnect(); lfoDepth.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      // ── Night insects: cricket chorus (gated by timeOfDay 20:00-05:00) ────
      case "amb_night_insects": {
        // Two cricket frequencies with fast 8Hz AM
        const osc1 = ctx.createOscillator();
        osc1.type = "sine";
        osc1.frequency.value = 4200;
        const osc1Gain = ctx.createGain();
        osc1Gain.gain.value = 0.002;

        const am1 = ctx.createOscillator();
        am1.type = "sine";
        am1.frequency.value = 8;
        const am1Depth = ctx.createGain();
        am1Depth.gain.value = 0.002; // full AM depth — chirping on/off
        am1.connect(am1Depth);
        am1Depth.connect(osc1Gain.gain);

        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.value = 4400;
        const osc2Gain = ctx.createGain();
        osc2Gain.gain.value = 0.002;

        const am2 = ctx.createOscillator();
        am2.type = "sine";
        am2.frequency.value = 8;
        am2.detune.value = 30; // slightly detuned AM for organic variation
        const am2Depth = ctx.createGain();
        am2Depth.gain.value = 0.002;
        am2.connect(am2Depth);
        am2Depth.connect(osc2Gain.gain);

        osc1.connect(osc1Gain);
        osc2.connect(osc2Gain);

        const gain = ctx.createGain();
        gain.gain.value = 1;
        osc1Gain.connect(gain);
        osc2Gain.connect(gain);

        osc1.start();
        am1.start();
        osc2.start();
        am2.start();
        this.ambientNodeCount += 4;

        return {
          source: osc1, gain,
          dispose: () => {
            osc1.stop(); osc1.disconnect(); osc1Gain.disconnect();
            am1.stop(); am1.disconnect(); am1Depth.disconnect();
            osc2.stop(); osc2.disconnect(); osc2Gain.disconnect();
            am2.stop(); am2.disconnect(); am2Depth.disconnect();
            this.ambientNodeCount -= 4;
          },
        };
      }

      // ── Morning birds: dawn birdsong via scheduled chirp sweeps ────────────
      case "amb_morning_birds": {
        // A placeholder sine that stays quiet — the actual chirps are
        // scheduled via _scheduleBirdCall() from the update loop.
        // We use a very quiet base tone to keep the layer alive.
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 1600;
        const gain = ctx.createGain();
        gain.gain.value = 0; // silent carrier — chirps are one-shot scheduled
        osc.connect(gain);
        osc.start();
        this.ambientNodeCount++;

        // Seed the bird call timer
        this._nextBirdTime = ctx.currentTime + 0.5 + Math.random() * 2;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); osc.disconnect();
            this.ambientNodeCount--;
          },
        };
      }

      // ── Creature purr: continuous gentle rumble for bonded sleeping cats ──
      case "amb_creature_purr": {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 25;

        // 25Hz amplitude modulation — the characteristic purr flutter
        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 25;

        const lfoGain = ctx.createGain();
        lfoGain.gain.value = 0.5; // AM depth

        const gain = ctx.createGain();
        gain.gain.value = 0.012;

        lfo.connect(lfoGain);
        lfoGain.connect(gain.gain);
        osc.connect(gain);
        osc.start();
        lfo.start();
        this.ambientNodeCount += 2;

        return {
          source: osc, gain,
          dispose: () => {
            osc.stop(); lfo.stop();
            osc.disconnect(); lfo.disconnect(); lfoGain.disconnect();
            this.ambientNodeCount -= 2;
          },
        };
      }

      default:
        return null;
    }
  }

  // ─── Still Room Swell Scheduling ─────────────────────────────────────────────

  /**
   * Check if it's time to fire a slow noise swell for the "still room" ambient.
   * Called every frame from update(). Uses AudioContext clock for precision.
   * Each swell: fade in 2s → hold 1s → fade out 3s → next in 8-15s.
   */
  private _scheduleStillSwell(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (!this.activeAmbient.has("amb_still_room")) return;
    if (ctx.currentTime < this._nextSwellTime) return;

    const layer = this.activeAmbient.get("amb_still_room");
    if (!layer) return;
    const gain = layer.gain;

    const peakGain  = 0.015;
    const fadeIn    = 2.0;
    const hold      = 1.0;
    const fadeOut   = 3.0;

    const now = ctx.currentTime;
    try {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peakGain, now + fadeIn);
      gain.gain.setValueAtTime(peakGain, now + fadeIn + hold);
      gain.gain.linearRampToValueAtTime(0, now + fadeIn + hold + fadeOut);
    } catch {
      // Gain node already disconnected — stop scheduling
      return;
    }

    const cycleDuration = fadeIn + hold + fadeOut;
    this._nextSwellTime = now + cycleDuration + 8 + Math.random() * 7;
  }

  // ─── Bird Call Scheduling ──────────────────────────────────────────────────

  /**
   * Schedule a single bird chirp sweep for the morning_birds ambient.
   * Each chirp: oscillator sweeps from ~2000Hz down to ~1200Hz over 0.08s.
   * Repeated every 2-5s randomly while the layer is active.
   */
  private _scheduleBirdCall(): void {
    const ctx = this.ctx;
    const ambientGain = this.ambientGain;
    if (!ctx || !ambientGain) return;
    if (!this.activeAmbient.has("amb_morning_birds")) return;
    if (ctx.currentTime < this._nextBirdTime) return;
    if (this.ambientNodeCount >= MAX_AMBIENT_NODES) return;

    const now = ctx.currentTime;

    // Randomize the chirp slightly for organic feel
    const startFreq = 1800 + Math.random() * 400;  // 1800-2200Hz
    const endFreq = 1000 + Math.random() * 400;    // 1000-1400Hz
    const duration = 0.06 + Math.random() * 0.04;  // 0.06-0.10s

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + duration);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.015, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + duration + 0.04);

    osc.connect(env);
    env.connect(ambientGain);
    osc.start(now);
    osc.stop(now + duration + 0.05);
    this.ambientNodeCount++;

    osc.addEventListener("ended", () => {
      osc.disconnect(); env.disconnect();
      this.ambientNodeCount--;
    });

    // Sometimes do a quick second chirp (double call)
    if (Math.random() < 0.4 && this.ambientNodeCount < MAX_AMBIENT_NODES) {
      const t2 = now + duration + 0.08;
      const osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(startFreq * 1.1, t2);
      osc2.frequency.exponentialRampToValueAtTime(endFreq * 1.1, t2 + duration * 0.8);

      const env2 = ctx.createGain();
      env2.gain.setValueAtTime(0.01, t2);
      env2.gain.exponentialRampToValueAtTime(0.0001, t2 + duration * 0.8 + 0.03);

      osc2.connect(env2);
      env2.connect(ambientGain);
      osc2.start(t2);
      osc2.stop(t2 + duration + 0.05);
      this.ambientNodeCount++;

      osc2.addEventListener("ended", () => {
        osc2.disconnect(); env2.disconnect();
        this.ambientNodeCount--;
      });
    }

    // Next bird call: 2-5 seconds
    this._nextBirdTime = now + 2 + Math.random() * 3;
  }

  // ─── Fire Crackle Scheduling ──────────────────────────────────────────────

  /**
   * Check if it's time to fire a crackle burst and schedule the next one.
   * Called every frame from update(). Uses Web Audio clock for precision.
   */
  private _scheduleCrackle(): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;

    // Only crackle if the hearth layer is active
    const hearthActive =
      this.activeAmbient.has("amb_cozy_hearth") ||
      this.activeAmbient.has("sfx_fire_crackle");
    if (!hearthActive) return;

    if (ctx.currentTime < this.nextCrackleTime) return;

    // Schedule crackle burst
    this._playCrackle(ctx, sfxGain);

    // Random interval: 0.3-2 seconds
    this.nextCrackleTime = ctx.currentTime + 0.3 + Math.random() * 1.7;
  }

  /**
   * Play a single fire crackle burst.
   * Occasionally plays a louder "pop" for variety.
   */
  private _playCrackle(ctx: AudioContext, destination: AudioNode): void {
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;
    const isPop = Math.random() < 0.08; // 8% chance of a proper pop

    const source = this._whiteSource(ctx);
    const bpf = createBPF(ctx, isPop ? 1500 : 2000, isPop ? 6 : 2);
    const env = ctx.createGain();

    const peakGain  = isPop ? 0.06 : (0.02 + Math.random() * 0.04);
    const decayTime = isPop ? 0.04 : (0.05 + Math.random() * 0.10);

    const now = ctx.currentTime;
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(peakGain, now + 0.002); // sharp attack
    env.gain.exponentialRampToValueAtTime(0.0001, now + decayTime);

    source.connect(bpf);
    bpf.connect(env);
    env.connect(destination);

    source.start(now);
    source.stop(now + decayTime + 0.01);
    this.sfxNodeCount++;

    source.addEventListener("ended", () => {
      source.disconnect();
      bpf.disconnect();
      env.disconnect();
      this.sfxNodeCount--;
    });
  }

  // ─── Record Player Note Scheduling ───────────────────────────────────────────

  /**
   * Schedule a single pentatonic note on the record player.
   * Only fires when the record player furniture is present and on.
   */
  private _scheduleRecordNote(): void {
    const ctx = this.ctx;
    const ambientGain = this.ambientGain;
    if (!ctx || !ambientGain) return;

    const recordOn =
      this.furniture.types.has("record_player") &&
      this.furniture.states.get("record_player")?.["on"] !== false;
    if (!recordOn) return;

    if (ctx.currentTime < this.nextNoteTime) return;
    if (this.ambientNodeCount >= MAX_AMBIENT_NODES) return;

    const freq = PENTATONIC_C4[Math.floor(Math.random() * PENTATONIC_C4.length)];

    for (let v = 0; v < 3; v++) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq * (1 + (v - 1) * 0.003);

      const lpf = createLPF(ctx, 2000, 0.9);
      const env = ctx.createGain();

      const now = ctx.currentTime;
      env.gain.setValueAtTime(0.03 / 3, now);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 1.5);

      osc.connect(lpf);
      lpf.connect(env);
      env.connect(ambientGain);

      osc.start(now);
      osc.stop(now + 1.6);
      this.ambientNodeCount++;

      osc.addEventListener("ended", () => {
        osc.disconnect();
        lpf.disconnect();
        env.disconnect();
        this.ambientNodeCount--;
      });
    }

    // Schedule next note: 1.5-3 seconds
    this.nextNoteTime = ctx.currentTime + 1.5 + Math.random() * 1.5;
  }

  // ─── Fish Tank Bubble Scheduling ─────────────────────────────────────────────

  /**
   * Schedule a fish tank bubble sweep.
   * Only fires when a fish_tank is present.
   */
  private _scheduleBubble(): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;

    if (!this.furniture.types.has("fish_tank")) return;
    if (ctx.currentTime < this.nextBubbleTime) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.linearRampToValueAtTime(600, now + 0.06);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.015, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

    osc.connect(env);
    env.connect(sfxGain);
    osc.start(now);
    osc.stop(now + 0.09);
    this.sfxNodeCount++;

    osc.addEventListener("ended", () => {
      osc.disconnect();
      env.disconnect();
      this.sfxNodeCount--;
    });

    // Next bubble: 2-5 seconds
    this.nextBubbleTime = ctx.currentTime + 2 + Math.random() * 3;
  }

  // ─── One-shot Sounds ──────────────────────────────────────────────────────────

  /**
   * Play a one-shot footstep sound appropriate to the tile material.
   */
  private _playFootstep(tileType: string): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;
    this.sfxNodeCount++;

    const now = ctx.currentTime;

    switch (tileType) {

      case "wood":
      case "dock": {
        // Short percussive thud with wooden resonance (dock is weathered wood)
        const source = this._whiteSource(ctx);
        const bpf = createBPF(ctx, tileType === "dock" ? 350 : 400, 3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(tileType === "dock" ? 0.05 : 0.04, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.04);
        source.addEventListener("ended", () => { source.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "stone":
      case "cobblestone":
      case "stone_dark":
      case "marble": {
        // Sharp click — harder for stone, softer for marble
        const freq = tileType === "marble" ? 1000 : tileType === "cobblestone" ? 900 : 1200;
        const vol = tileType === "marble" ? 0.04 : 0.05;
        const source = this._whiteSource(ctx);
        const bpf = createBPF(ctx, freq, 5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.025);
        source.addEventListener("ended", () => { source.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "carpet": {
        // Very soft, muffled thud
        const source = this._brownSource(ctx);
        const lpf = createLPF(ctx, 200, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        source.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.045);
        source.addEventListener("ended", () => { source.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "grass":
      case "dirt": {
        // Airy rustle for grass, slightly duller for dirt
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, tileType === "dirt" ? 800 : 2000, 0.7);
        const lpf = createLPF(ctx, tileType === "dirt" ? 3000 : 6000, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(tileType === "dirt" ? 0.035 : 0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
        source.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.055);
        source.addEventListener("ended", () => {
          source.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--;
        });
        break;
      }

      case "sand": {
        // Soft, muffled crunch — very low frequency
        const source = this._brownSource(ctx);
        const lpf = createLPF(ctx, 400, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.025, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        source.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.065);
        source.addEventListener("ended", () => { source.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "snow": {
        // Crisp crunch — high-frequency white noise, quick decay
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 3000, 1.0);
        const lpf = createLPF(ctx, 8000, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        source.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.045);
        source.addEventListener("ended", () => {
          source.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--;
        });
        break;
      }

      case "crystal": {
        // Crystalline chime — subtle ring on step
        const source = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 2400, 8);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.065);
        source.addEventListener("ended", () => { source.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      default: {
        // Generic soft tap
        const source = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 600, 2);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.025, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.035);
        source.addEventListener("ended", () => { source.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
    }
  }

  /**
   * Play a one-shot interaction sound.
   */
  private _playInteraction(type: string): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;

    switch (type) {

      case "toggle": {
        this.sfxNodeCount++;
        // Soft click (noise) + warm tone sweep
        const click = this._whiteSource(ctx);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.05, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.001);
        click.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.002);
        click.addEventListener("ended", () => { click.disconnect(); clickEnv.disconnect(); });

        // Warm tone sweep 200 → 300Hz
        const tone = ctx.createOscillator();
        tone.type = "sine";
        tone.frequency.setValueAtTime(200, now);
        tone.frequency.linearRampToValueAtTime(300, now + 0.1);
        const toneEnv = ctx.createGain();
        toneEnv.gain.setValueAtTime(0.05, now);
        toneEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        tone.connect(toneEnv); toneEnv.connect(sfxGain);
        tone.start(now); tone.stop(now + 0.11);
        tone.addEventListener("ended", () => { tone.disconnect(); toneEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "sit": {
        this.sfxNodeCount++;
        // Soft creak: noise through mid BPF with slow decay
        const source = this._brownSource(ctx);
        const bpf = createBPF(ctx, 500, 3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.22);
        source.addEventListener("ended", () => { source.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "piano": {
        this.sfxNodeCount++;
        // C major chord: C4 + E4 + G4, slow decay
        const chord = [261.63, 329.63, 392.00];
        chord.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          env.gain.setValueAtTime(0.04 / chord.length, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 2.1);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === chord.length - 1) this.sfxNodeCount--; });
        });
        break;
      }

      // ── Device sound effects ──────────────────────────────────────────

      case "device_open": {
        this.sfxNodeCount++;
        // Upward swoosh (filtered noise) + crisp click + subtle tone rise
        // The sound of pulling a phone from your pocket and the screen waking

        // Click: tight noise burst
        const click = this._whiteSource(ctx);
        const clickBPF = ctx.createBiquadFilter();
        clickBPF.type = "bandpass"; clickBPF.frequency.value = 3000; clickBPF.Q.value = 2;
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.06, now + 0.02);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now + 0.02); click.stop(now + 0.04);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); });

        // Swoosh: rising filtered noise
        const swoosh = this._brownSource(ctx);
        const swooshBPF = ctx.createBiquadFilter();
        swooshBPF.type = "bandpass"; swooshBPF.Q.value = 1.5;
        swooshBPF.frequency.setValueAtTime(400, now);
        swooshBPF.frequency.exponentialRampToValueAtTime(2000, now + 0.12);
        const swooshEnv = ctx.createGain();
        swooshEnv.gain.setValueAtTime(0.03, now);
        swooshEnv.gain.linearRampToValueAtTime(0.05, now + 0.04);
        swooshEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        swoosh.connect(swooshBPF); swooshBPF.connect(swooshEnv); swooshEnv.connect(sfxGain);
        swoosh.start(now); swoosh.stop(now + 0.16);
        swoosh.addEventListener("ended", () => { swoosh.disconnect(); swooshBPF.disconnect(); swooshEnv.disconnect(); });

        // Wake tone: two-note ascending chime (like a screen turning on)
        [660, 880].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + 0.05 + i * 0.06;
          env.gain.setValueAtTime(0, t);
          env.gain.linearRampToValueAtTime(0.025, t + 0.01);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.16);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "device_close": {
        this.sfxNodeCount++;
        // Downward swoosh + muted click — the sound of putting the phone away

        // Descending swoosh
        const swoosh = this._brownSource(ctx);
        const bpf = ctx.createBiquadFilter();
        bpf.type = "bandpass"; bpf.Q.value = 1.5;
        bpf.frequency.setValueAtTime(1800, now);
        bpf.frequency.exponentialRampToValueAtTime(300, now + 0.12);
        const swooshEnv = ctx.createGain();
        swooshEnv.gain.setValueAtTime(0.04, now);
        swooshEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        swoosh.connect(bpf); bpf.connect(swooshEnv); swooshEnv.connect(sfxGain);
        swoosh.start(now); swoosh.stop(now + 0.15);
        swoosh.addEventListener("ended", () => { swoosh.disconnect(); bpf.disconnect(); swooshEnv.disconnect(); });

        // Muted thud — phone sliding into pocket
        const thud = this._brownSource(ctx);
        const thudLPF = ctx.createBiquadFilter();
        thudLPF.type = "lowpass"; thudLPF.frequency.value = 400;
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.04, now + 0.08);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        thud.connect(thudLPF); thudLPF.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now + 0.08); thud.stop(now + 0.15);
        thud.addEventListener("ended", () => { thud.disconnect(); thudLPF.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "device_tap": {
        this.sfxNodeCount++;
        // Soft haptic-style tap — like tapping a glass screen
        const tap = this._whiteSource(ctx);
        const tapBPF = ctx.createBiquadFilter();
        tapBPF.type = "bandpass"; tapBPF.frequency.value = 4000; tapBPF.Q.value = 4;
        const tapEnv = ctx.createGain();
        tapEnv.gain.setValueAtTime(0.025, now);
        tapEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        tap.connect(tapBPF); tapBPF.connect(tapEnv); tapEnv.connect(sfxGain);
        tap.start(now); tap.stop(now + 0.02);
        tap.addEventListener("ended", () => { tap.disconnect(); tapBPF.disconnect(); tapEnv.disconnect(); });

        // Subtle resonance — the glass vibrating
        const res = ctx.createOscillator();
        res.type = "sine";
        res.frequency.value = 2200;
        const resEnv = ctx.createGain();
        resEnv.gain.setValueAtTime(0.008, now);
        resEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        res.connect(resEnv); resEnv.connect(sfxGain);
        res.start(now); res.stop(now + 0.05);
        res.addEventListener("ended", () => { res.disconnect(); resEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "device_notification": {
        this.sfxNodeCount++;
        // Warm two-tone chime — friendly, not urgent. Like a gentle bell.
        const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 — major chord ascending
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.08;
          env.gain.setValueAtTime(0, t);
          env.gain.linearRampToValueAtTime(0.03, t + 0.01);
          env.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
          env.gain.linearRampToValueAtTime(0, t + 0.35);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.36);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); });
        });

        // Shimmer: gentle high noise burst
        const shimmer = this._whiteSource(ctx);
        const shimBPF = ctx.createBiquadFilter();
        shimBPF.type = "bandpass"; shimBPF.frequency.value = 6000; shimBPF.Q.value = 3;
        const shimEnv = ctx.createGain();
        shimEnv.gain.setValueAtTime(0.008, now);
        shimEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        shimmer.connect(shimBPF); shimBPF.connect(shimEnv); shimEnv.connect(sfxGain);
        shimmer.start(now); shimmer.stop(now + 0.22);
        shimmer.addEventListener("ended", () => { shimmer.disconnect(); shimBPF.disconnect(); shimEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "device_app_open": {
        this.sfxNodeCount++;
        // Quick ascending slide — navigating into an app
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(700, now + 0.08);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.11);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "device_back": {
        this.sfxNodeCount++;
        // Quick descending slide — navigating back
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(350, now + 0.08);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.11);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      // ── UI Interaction Sounds ────────────────────────────────────────

      case "ui_click": {
        this.sfxNodeCount++;
        // Soft wood click: short filtered noise burst through bandpass
        const click = this._whiteSource(ctx);
        const clickBPF = ctx.createBiquadFilter();
        clickBPF.type = "bandpass"; clickBPF.frequency.value = 1500; clickBPF.Q.value = 3;
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.03, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.05);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "ui_hover": {
        this.sfxNodeCount++;
        // Ultra-subtle sine whisper — barely audible feedback
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 880;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.008, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.03);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "ui_open": {
        this.sfxNodeCount++;
        // Two ascending wood tones — like a wooden drawer sliding open
        [300, 500].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const bpf = ctx.createBiquadFilter();
          bpf.type = "bandpass"; bpf.frequency.value = freq; bpf.Q.value = 2;
          const env = ctx.createGain();
          const t = now + i * 0.015;
          env.gain.setValueAtTime(0.04, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); bpf.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "ui_close": {
        this.sfxNodeCount++;
        // Two descending wood tones — reverse of open
        [500, 300].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const bpf = ctx.createBiquadFilter();
          bpf.type = "bandpass"; bpf.frequency.value = freq; bpf.Q.value = 2;
          const env = ctx.createGain();
          const t = now + i * 0.015;
          env.gain.setValueAtTime(0.04, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); bpf.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "ui_tab": {
        this.sfxNodeCount++;
        // Quick crisp tick — like flipping a wooden tab
        const tick = this._whiteSource(ctx);
        const tickBPF = ctx.createBiquadFilter();
        tickBPF.type = "bandpass"; tickBPF.frequency.value = 2000; tickBPF.Q.value = 4;
        const tickEnv = ctx.createGain();
        tickEnv.gain.setValueAtTime(0.025, now);
        tickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);
        tick.connect(tickBPF); tickBPF.connect(tickEnv); tickEnv.connect(sfxGain);
        tick.start(now); tick.stop(now + 0.03);
        tick.addEventListener("ended", () => { tick.disconnect(); tickBPF.disconnect(); tickEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "ui_equip": {
        this.sfxNodeCount++;
        // Low-mid thud + metallic ring — like slotting armor onto a wooden mannequin
        const thud = ctx.createOscillator();
        thud.type = "sine";
        thud.frequency.value = 120;
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.05, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        thud.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.07);
        thud.addEventListener("ended", () => { thud.disconnect(); thudEnv.disconnect(); });

        // Metallic ring
        const ring = ctx.createOscillator();
        ring.type = "sine";
        ring.frequency.value = 1200;
        const ringEnv = ctx.createGain();
        ringEnv.gain.setValueAtTime(0.05, now);
        ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        ring.connect(ringEnv); ringEnv.connect(sfxGain);
        ring.start(now); ring.stop(now + 0.16);
        ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "ui_error": {
        this.sfxNodeCount++;
        // Gentle two-note descending minor interval — a soft wooden "nope"
        [440, 370].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.12;
          env.gain.setValueAtTime(0.03, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.16);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "heartbeat": {
        this.sfxNodeCount++;
        // Deep double-thump — for low-health warning loop
        [0, 0.1].forEach((delay) => {
          const pulse = ctx.createOscillator();
          pulse.type = "sine";
          pulse.frequency.value = 50;
          const env = ctx.createGain();
          const t = now + delay;
          env.gain.setValueAtTime(0.06, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
          pulse.connect(env); env.connect(sfxGain);
          pulse.start(t); pulse.stop(t + 0.12);
          pulse.addEventListener("ended", () => { pulse.disconnect(); env.disconnect(); if (delay > 0) this.sfxNodeCount--; });
        });
        break;
      }

      // ── Combat Stingers ────────────────────────────────────────────────

      case "combat_enter": {
        this.sfxNodeCount++;
        // Rising tension — two ascending tones (C4 → E4) with noise burst
        const osc1 = ctx.createOscillator();
        osc1.type = "sawtooth";
        osc1.frequency.setValueAtTime(220, now);
        osc1.frequency.exponentialRampToValueAtTime(330, now + 0.2);
        const env1 = ctx.createGain();
        env1.gain.setValueAtTime(0.04, now);
        env1.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        const lpf = createLPF(ctx, 1200);
        osc1.connect(lpf); lpf.connect(env1); env1.connect(sfxGain);
        osc1.start(now); osc1.stop(now + 0.4);
        osc1.addEventListener("ended", () => { osc1.disconnect(); env1.disconnect(); lpf.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_exit": {
        this.sfxNodeCount++;
        // Relief — soft descending tone (G4 → C4) with gentle fade
        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(392, now);
        osc2.frequency.exponentialRampToValueAtTime(262, now + 0.4);
        const env2 = ctx.createGain();
        env2.gain.setValueAtTime(0.03, now);
        env2.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        osc2.connect(env2); env2.connect(sfxGain);
        osc2.start(now); osc2.stop(now + 0.55);
        osc2.addEventListener("ended", () => { osc2.disconnect(); env2.disconnect(); this.sfxNodeCount--; });
        break;
      }

      // ── Combat Sounds ──────────────────────────────────────────────────

      case "combat_hit": {
        // Meaty impact: filtered noise burst (lowpass 800Hz) + low thud (sine 80Hz)
        const noise = this._whiteSource(ctx);
        const lpf = createLPF(ctx, 800);
        const noiseEnv = ctx.createGain();
        noiseEnv.gain.setValueAtTime(0.06, now);
        noiseEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        noise.connect(lpf); lpf.connect(noiseEnv); noiseEnv.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.14);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); lpf.disconnect(); noiseEnv.disconnect(); this.sfxNodeCount--; });

        // Low thud
        const thud = ctx.createOscillator();
        thud.type = "sine";
        thud.frequency.setValueAtTime(80, now);
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.05, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        thud.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.14);
        this.sfxNodeCount++;
        thud.addEventListener("ended", () => { thud.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_swing": {
        // Whoosh: bandpass-filtered noise sweep 1000→3000Hz over 0.1s
        const whoosh = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 1000, 2);
        bpf.frequency.setValueAtTime(1000, now);
        bpf.frequency.exponentialRampToValueAtTime(3000, now + 0.1);
        const whooshEnv = ctx.createGain();
        whooshEnv.gain.setValueAtTime(0.03, now);
        whooshEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        whoosh.connect(bpf); bpf.connect(whooshEnv); whooshEnv.connect(sfxGain);
        whoosh.start(now); whoosh.stop(now + 0.14);
        this.sfxNodeCount++;
        whoosh.addEventListener("ended", () => { whoosh.disconnect(); bpf.disconnect(); whooshEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_crit": {
        // Metallic ring on top of hit: sine 1200Hz with fast decay
        const ring = ctx.createOscillator();
        ring.type = "sine";
        ring.frequency.value = 1200;
        const ringEnv = ctx.createGain();
        ringEnv.gain.setValueAtTime(0.04, now);
        ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        ring.connect(ringEnv); ringEnv.connect(sfxGain);
        ring.start(now); ring.stop(now + 0.17);
        this.sfxNodeCount++;
        ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); this.sfxNodeCount--; });

        // Plus the hit sound: filtered noise burst + low thud
        const noise = this._whiteSource(ctx);
        const lpf = createLPF(ctx, 800);
        const noiseEnv = ctx.createGain();
        noiseEnv.gain.setValueAtTime(0.06, now);
        noiseEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        noise.connect(lpf); lpf.connect(noiseEnv); noiseEnv.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.14);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); lpf.disconnect(); noiseEnv.disconnect(); this.sfxNodeCount--; });

        const thud = ctx.createOscillator();
        thud.type = "sine";
        thud.frequency.setValueAtTime(80, now);
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.05, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        thud.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.14);
        this.sfxNodeCount++;
        thud.addEventListener("ended", () => { thud.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_death": {
        // Descending sweep: sine 400→80Hz over 0.5s with slow fade
        const sweep = ctx.createOscillator();
        sweep.type = "sine";
        sweep.frequency.setValueAtTime(400, now);
        sweep.frequency.exponentialRampToValueAtTime(80, now + 0.5);
        const sweepEnv = ctx.createGain();
        sweepEnv.gain.setValueAtTime(0.04, now);
        sweepEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
        sweep.connect(sweepEnv); sweepEnv.connect(sfxGain);
        sweep.start(now); sweep.stop(now + 0.57);
        this.sfxNodeCount++;
        sweep.addEventListener("ended", () => { sweep.disconnect(); sweepEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_player_hurt": {
        // Deeper impact: filtered noise at 400Hz + sine 60Hz, louder
        const noise = this._whiteSource(ctx);
        const lpf = createLPF(ctx, 400);
        const noiseEnv = ctx.createGain();
        noiseEnv.gain.setValueAtTime(0.06, now);
        noiseEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        noise.connect(lpf); lpf.connect(noiseEnv); noiseEnv.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.17);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); lpf.disconnect(); noiseEnv.disconnect(); this.sfxNodeCount--; });

        const bass = ctx.createOscillator();
        bass.type = "sine";
        bass.frequency.setValueAtTime(60, now);
        const bassEnv = ctx.createGain();
        bassEnv.gain.setValueAtTime(0.06, now);
        bassEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        bass.connect(bassEnv); bassEnv.connect(sfxGain);
        bass.start(now); bass.stop(now + 0.17);
        this.sfxNodeCount++;
        bass.addEventListener("ended", () => { bass.disconnect(); bassEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      // ── Duel Sounds ───────────────────────────────────────────────────

      case "duel_request": {
        this.sfxNodeCount++;
        // Iconic sword-cross "SHING" — two metallic tones crossing with
        // bright transient. Harmonics at 2kHz + 4kHz + noise burst.
        // This is the sound that makes every player's head turn.
        [2000, 4000, 6000].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq * (1 + i * 0.02), now);
          osc.frequency.exponentialRampToValueAtTime(freq * 0.7, now + 0.3);
          const env = ctx.createGain();
          const vol = [0.04, 0.025, 0.015][i];
          env.gain.setValueAtTime(vol, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4 - i * 0.05);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 0.45);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); });
        });
        // Bright transient — highpass-filtered noise click
        const click = this._whiteSource(ctx);
        const hpf = ctx.createBiquadFilter();
        hpf.type = "highpass"; hpf.frequency.value = 4000; hpf.Q.value = 1;
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.06, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        click.connect(hpf); hpf.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.03);
        this.sfxNodeCount++;
        click.addEventListener("ended", () => { click.disconnect(); hpf.disconnect(); clickEnv.disconnect(); this.sfxNodeCount--; });
        // Sub resonance — the weight behind the blade
        const sub = ctx.createOscillator();
        sub.type = "sine"; sub.frequency.value = 120;
        const subEnv = ctx.createGain();
        subEnv.gain.setValueAtTime(0.03, now);
        subEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        sub.connect(subEnv); subEnv.connect(sfxGain);
        sub.start(now); sub.stop(now + 0.17);
        sub.addEventListener("ended", () => { sub.disconnect(); subEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "duel_countdown": {
        this.sfxNodeCount++;
        // War drum tick — deep filtered thud at 100Hz + skin noise at 800Hz
        const drum = ctx.createOscillator();
        drum.type = "sine";
        drum.frequency.setValueAtTime(100, now);
        drum.frequency.exponentialRampToValueAtTime(60, now + 0.08);
        const drumEnv = ctx.createGain();
        drumEnv.gain.setValueAtTime(0.07, now);
        drumEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        drum.connect(drumEnv); drumEnv.connect(sfxGain);
        drum.start(now); drum.stop(now + 0.22);
        drum.addEventListener("ended", () => { drum.disconnect(); drumEnv.disconnect(); });
        // Skin slap — bandpass noise
        const skin = this._whiteSource(ctx);
        const skinBPF = createBPF(ctx, 800, 3);
        const skinEnv = ctx.createGain();
        skinEnv.gain.setValueAtTime(0.04, now);
        skinEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        skin.connect(skinBPF); skinBPF.connect(skinEnv); skinEnv.connect(sfxGain);
        skin.start(now); skin.stop(now + 0.05);
        this.sfxNodeCount++;
        skin.addEventListener("ended", () => { skin.disconnect(); skinBPF.disconnect(); skinEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "duel_fight": {
        this.sfxNodeCount++;
        // War horn blast — sawtooth 220Hz→330Hz with rich harmonics
        // The "FIGHT!" moment. Visceral and unmistakable.
        const horn = ctx.createOscillator();
        horn.type = "sawtooth";
        horn.frequency.setValueAtTime(220, now);
        horn.frequency.exponentialRampToValueAtTime(330, now + 0.15);
        horn.frequency.setValueAtTime(330, now + 0.15);
        const hornLPF = createLPF(ctx, 2000);
        const hornEnv = ctx.createGain();
        hornEnv.gain.setValueAtTime(0, now);
        hornEnv.gain.linearRampToValueAtTime(0.06, now + 0.05);
        hornEnv.gain.setValueAtTime(0.06, now + 0.3);
        hornEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
        horn.connect(hornLPF); hornLPF.connect(hornEnv); hornEnv.connect(sfxGain);
        horn.start(now); horn.stop(now + 0.65);
        this.sfxNodeCount++;
        horn.addEventListener("ended", () => { horn.disconnect(); hornLPF.disconnect(); hornEnv.disconnect(); this.sfxNodeCount--; });
        // Second voice — perfect fifth above (E4)
        const horn2 = ctx.createOscillator();
        horn2.type = "sawtooth";
        horn2.frequency.setValueAtTime(330, now + 0.05);
        horn2.frequency.exponentialRampToValueAtTime(440, now + 0.2);
        const horn2LPF = createLPF(ctx, 1800);
        const horn2Env = ctx.createGain();
        horn2Env.gain.setValueAtTime(0, now + 0.05);
        horn2Env.gain.linearRampToValueAtTime(0.03, now + 0.1);
        horn2Env.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
        horn2.connect(horn2LPF); horn2LPF.connect(horn2Env); horn2Env.connect(sfxGain);
        horn2.start(now + 0.05); horn2.stop(now + 0.6);
        this.sfxNodeCount++;
        horn2.addEventListener("ended", () => { horn2.disconnect(); horn2LPF.disconnect(); horn2Env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "duel_victory": {
        this.sfxNodeCount++;
        // Triumphant brass fanfare — ascending major triad (C4→E4→G4→C5)
        // Each note slightly staggered, with a bright sustain
        const notes = [262, 330, 392, 523];
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sawtooth";
          osc.frequency.value = freq;
          const lpf = createLPF(ctx, 1600 + i * 200);
          const env = ctx.createGain();
          const t = now + i * 0.08;
          env.gain.setValueAtTime(0, t);
          env.gain.linearRampToValueAtTime(0.035, t + 0.03);
          env.gain.setValueAtTime(0.035, t + 0.25);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
          osc.connect(lpf); lpf.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.55);
          osc.addEventListener("ended", () => { osc.disconnect(); lpf.disconnect(); env.disconnect(); if (i === notes.length - 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "duel_defeat": {
        this.sfxNodeCount++;
        // Muted descending minor — Cm chord dissolving (C4→Eb4→G3)
        const notes = [262, 311, 196];
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now);
          osc.frequency.exponentialRampToValueAtTime(freq * 0.7, now + 0.6);
          const env = ctx.createGain();
          const t = now + i * 0.06;
          env.gain.setValueAtTime(0.025, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.55);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === notes.length - 1) this.sfxNodeCount--; });
        });
        break;
      }

      // ── Party Sounds ─────────────────────────────────────────────────

      case "party_join": {
        this.sfxNodeCount++;
        // Bright ascending chime — welcoming, warm
        [440, 554, 659].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.06;
          env.gain.setValueAtTime(0.03, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.25);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 2) this.sfxNodeCount--; });
        });
        break;
      }

      case "party_leave": {
        this.sfxNodeCount++;
        // Soft descending two-note — bittersweet departure
        [440, 330].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.1;
          env.gain.setValueAtTime(0.025, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.3);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "ready_check": {
        this.sfxNodeCount++;
        // Attention ping — two quick bright tones, impossible to miss
        [880, 1100].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.08;
          env.gain.setValueAtTime(0.04, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.12);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      // ── Level / Loot / Buff Sounds ───────────────────────────────────

      case "level_up": {
        this.sfxNodeCount++;
        // Triumphant ascending fanfare — C4→E4→G4→C5 brass chord with
        // shimmering harmonics and a sub-bass foundation. The moment that
        // makes every grind worth it. Staggered onset, bright sustain,
        // gentle release. Layered with a harp glissando shimmer.
        const chord = [262, 330, 392, 523, 659]; // C4 E4 G4 C5 E5
        chord.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = i < 4 ? "sawtooth" : "sine";
          osc.frequency.setValueAtTime(freq, now + i * 0.06);
          osc.frequency.exponentialRampToValueAtTime(freq * 1.003, now + i * 0.06 + 0.4);
          const lpf = createLPF(ctx, 1800 + i * 200);
          const env = ctx.createGain();
          const t = now + i * 0.06;
          const vol = i < 3 ? 0.028 : (i === 3 ? 0.022 : 0.012);
          env.gain.setValueAtTime(0, t);
          env.gain.linearRampToValueAtTime(vol, t + 0.03);
          env.gain.setValueAtTime(vol, t + 0.3);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
          osc.connect(lpf); lpf.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.85);
          osc.addEventListener("ended", () => { osc.disconnect(); lpf.disconnect(); env.disconnect(); });
        });
        // Sub-bass rumble — foundation of triumph
        const sub = ctx.createOscillator();
        sub.type = "sine"; sub.frequency.value = 65;
        const subEnv = ctx.createGain();
        subEnv.gain.setValueAtTime(0, now);
        subEnv.gain.linearRampToValueAtTime(0.04, now + 0.1);
        subEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
        sub.connect(subEnv); subEnv.connect(sfxGain);
        sub.start(now); sub.stop(now + 0.65);
        sub.addEventListener("ended", () => { sub.disconnect(); subEnv.disconnect(); });
        // Harp shimmer — descending sparkles
        [1568, 1318, 1047, 880, 659].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + 0.15 + i * 0.04;
          env.gain.setValueAtTime(0.012, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.18);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 4) this.sfxNodeCount--; });
        });
        break;
      }

      case "loot_drop": {
        // Coin cascade — metallic chime followed by tinkling descent.
        // The Diablo "cha-ching" feeling — rewarding, Pavlovian.
        // Bright bell at 2kHz + descending crystalline notes
        const bell = ctx.createOscillator();
        bell.type = "sine";
        bell.frequency.setValueAtTime(2093, now);
        bell.frequency.exponentialRampToValueAtTime(1800, now + 0.15);
        const bellEnv = ctx.createGain();
        bellEnv.gain.setValueAtTime(0.04, now);
        bellEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        bell.connect(bellEnv); bellEnv.connect(sfxGain);
        bell.start(now); bell.stop(now + 0.28);
        bell.addEventListener("ended", () => { bell.disconnect(); bellEnv.disconnect(); });
        // Descending sparkle notes — coins falling
        [1568, 1318, 1175, 988].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + 0.04 + i * 0.05;
          env.gain.setValueAtTime(0.02, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.15);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); });
        });
        // Metallic transient — coin clatter
        const click = this._whiteSource(ctx);
        const hpf = ctx.createBiquadFilter();
        hpf.type = "highpass"; hpf.frequency.value = 5000; hpf.Q.value = 2;
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.035, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        click.connect(hpf); hpf.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.02);
        this.sfxNodeCount++;
        click.addEventListener("ended", () => { click.disconnect(); hpf.disconnect(); clickEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "buff_gain": {
        this.sfxNodeCount++;
        // Magical shimmer — ascending crystalline wash. The feeling of
        // power suffusing through you. Bright sine sweep with harmonics.
        const sweep = ctx.createOscillator();
        sweep.type = "sine";
        sweep.frequency.setValueAtTime(600, now);
        sweep.frequency.exponentialRampToValueAtTime(1400, now + 0.2);
        const sweepEnv = ctx.createGain();
        sweepEnv.gain.setValueAtTime(0, now);
        sweepEnv.gain.linearRampToValueAtTime(0.025, now + 0.03);
        sweepEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        sweep.connect(sweepEnv); sweepEnv.connect(sfxGain);
        sweep.start(now); sweep.stop(now + 0.32);
        sweep.addEventListener("ended", () => { sweep.disconnect(); sweepEnv.disconnect(); });
        // Harmonic overtone — octave above, delayed
        const harm = ctx.createOscillator();
        harm.type = "sine";
        harm.frequency.setValueAtTime(1200, now + 0.05);
        harm.frequency.exponentialRampToValueAtTime(2800, now + 0.25);
        const harmEnv = ctx.createGain();
        harmEnv.gain.setValueAtTime(0, now + 0.05);
        harmEnv.gain.linearRampToValueAtTime(0.012, now + 0.08);
        harmEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        harm.connect(harmEnv); harmEnv.connect(sfxGain);
        harm.start(now + 0.05); harm.stop(now + 0.28);
        harm.addEventListener("ended", () => { harm.disconnect(); harmEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "buff_fade": {
        this.sfxNodeCount++;
        // Soft descending dissolve — power draining away gently.
        // Descending sine with lowpass sweep.
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(300, now + 0.25);
        const lpf = createLPF(ctx, 1200);
        lpf.frequency.exponentialRampToValueAtTime(200, now + 0.25);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.018, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.32);
        osc.addEventListener("ended", () => { osc.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_dodge": {
        // Swift whoosh — bandpass noise sweep from 2kHz→5kHz over 0.08s.
        // The sound of air displacing as the player evades. Fast, sharp.
        const whoosh = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 2000, 1.5);
        bpf.frequency.exponentialRampToValueAtTime(5000, now + 0.08);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.05, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        whoosh.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        whoosh.start(now); whoosh.stop(now + 0.14);
        this.sfxNodeCount++;
        whoosh.addEventListener("ended", () => { whoosh.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_parry": {
        // Metallic clang — two detuned high-frequency tones + noise transient.
        // Steel meeting steel. The satisfying ring of a perfect parry.
        [1800, 2200].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now);
          osc.frequency.exponentialRampToValueAtTime(freq * 0.6, now + 0.2);
          const env = ctx.createGain();
          env.gain.setValueAtTime(0.035, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 0.28);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); });
        });
        // Impact transient
        const click = this._whiteSource(ctx);
        const hpf = ctx.createBiquadFilter();
        hpf.type = "highpass"; hpf.frequency.value = 3000; hpf.Q.value = 1.5;
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.05, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        click.connect(hpf); hpf.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.03);
        this.sfxNodeCount++;
        click.addEventListener("ended", () => { click.disconnect(); hpf.disconnect(); clickEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "combat_block": {
        // Heavy shield impact — deep thud + wooden resonance.
        // The satisfying THUNK of a shield catching a blow.
        const thud = ctx.createOscillator();
        thud.type = "sine";
        thud.frequency.setValueAtTime(120, now);
        thud.frequency.exponentialRampToValueAtTime(60, now + 0.08);
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.06, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        thud.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.18);
        thud.addEventListener("ended", () => { thud.disconnect(); thudEnv.disconnect(); });
        // Wood resonance — filtered noise body
        const wood = this._whiteSource(ctx);
        const woodBPF = createBPF(ctx, 600, 3);
        const woodEnv = ctx.createGain();
        woodEnv.gain.setValueAtTime(0.04, now);
        woodEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        wood.connect(woodBPF); woodBPF.connect(woodEnv); woodEnv.connect(sfxGain);
        wood.start(now); wood.stop(now + 0.07);
        this.sfxNodeCount++;
        wood.addEventListener("ended", () => { wood.disconnect(); woodBPF.disconnect(); woodEnv.disconnect(); this.sfxNodeCount--; });
        // Rim ring — bright metallic overtone from shield edge
        const rim = ctx.createOscillator();
        rim.type = "sine";
        rim.frequency.setValueAtTime(1400, now);
        rim.frequency.exponentialRampToValueAtTime(900, now + 0.12);
        const rimEnv = ctx.createGain();
        rimEnv.gain.setValueAtTime(0.02, now + 0.01);
        rimEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        rim.connect(rimEnv); rimEnv.connect(sfxGain);
        rim.start(now + 0.01); rim.stop(now + 0.18);
        rim.addEventListener("ended", () => { rim.disconnect(); rimEnv.disconnect(); });
        break;
      }

      // ── Emote Sounds ──────────────────────────────────────────────────

      case "emote_wave": {
        this.sfxNodeCount++;
        // Quick ascending arpeggio: C5→E5→G5, each note 0.04s
        [523.25, 659.25, 783.99].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.04;
          env.gain.setValueAtTime(0.02, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 2) this.sfxNodeCount--; });
        });
        break;
      }

      case "emote_bow": {
        this.sfxNodeCount++;
        // Descending gentle tone: 440→220Hz sine sweep over 0.3s
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.exponentialRampToValueAtTime(220, now + 0.3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.015, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.36);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_salute": {
        this.sfxNodeCount++;
        // Crisp click + short 880Hz tone
        const click = this._whiteSource(ctx);
        const clickBPF = createBPF(ctx, 3000, 4);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.03, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.01);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.015);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); });

        const tone = ctx.createOscillator();
        tone.type = "sine";
        tone.frequency.value = 880;
        const toneEnv = ctx.createGain();
        toneEnv.gain.setValueAtTime(0.02, now + 0.01);
        toneEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        tone.connect(toneEnv); toneEnv.connect(sfxGain);
        tone.start(now + 0.01); tone.stop(now + 0.09);
        tone.addEventListener("ended", () => { tone.disconnect(); toneEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_clap": {
        this.sfxNodeCount++;
        // Two rapid white noise bursts through BPF 2000Hz
        [0, 0.08].forEach((delay) => {
          const src = this._whiteSource(ctx);
          const bpf = createBPF(ctx, 2000, 2);
          const env = ctx.createGain();
          const t = now + delay;
          env.gain.setValueAtTime(0.04, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
          src.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          src.start(t); src.stop(t + 0.035);
          src.addEventListener("ended", () => { src.disconnect(); bpf.disconnect(); env.disconnect(); if (delay > 0) this.sfxNodeCount--; });
        });
        break;
      }

      case "emote_surprised": {
        this.sfxNodeCount++;
        // Quick ascending sweep: 300→800Hz over 0.1s
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + 0.1);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.025, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.16);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_shocked": {
        this.sfxNodeCount++;
        // Sharp noise burst + descending sweep 1000→200Hz over 0.2s
        const burst = this._whiteSource(ctx);
        const burstBPF = createBPF(ctx, 2500, 2);
        const burstEnv = ctx.createGain();
        burstEnv.gain.setValueAtTime(0.03, now);
        burstEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        burst.connect(burstBPF); burstBPF.connect(burstEnv); burstEnv.connect(sfxGain);
        burst.start(now); burst.stop(now + 0.035);
        burst.addEventListener("ended", () => { burst.disconnect(); burstBPF.disconnect(); burstEnv.disconnect(); });

        const sweep = ctx.createOscillator();
        sweep.type = "sine";
        sweep.frequency.setValueAtTime(1000, now);
        sweep.frequency.exponentialRampToValueAtTime(200, now + 0.2);
        const sweepEnv = ctx.createGain();
        sweepEnv.gain.setValueAtTime(0.03, now);
        sweepEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        sweep.connect(sweepEnv); sweepEnv.connect(sfxGain);
        sweep.start(now); sweep.stop(now + 0.26);
        sweep.addEventListener("ended", () => { sweep.disconnect(); sweepEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_laugh": {
        this.sfxNodeCount++;
        // Three quick ascending sine pops at 400/500/600Hz, 50ms apart
        [400, 500, 600].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.05;
          env.gain.setValueAtTime(0.02, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 2) this.sfxNodeCount--; });
        });
        break;
      }

      case "emote_cry": {
        this.sfxNodeCount++;
        // Slow descending sine 400→250Hz over 0.4s with 5Hz vibrato depth 20Hz
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(250, now + 0.4);

        const vibrato = ctx.createOscillator();
        vibrato.type = "sine";
        vibrato.frequency.value = 5;
        const vibDepth = ctx.createGain();
        vibDepth.gain.value = 20;
        vibrato.connect(vibDepth);
        vibDepth.connect(osc.frequency);

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.015, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); vibrato.start(now);
        osc.stop(now + 0.46); vibrato.stop(now + 0.46);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); vibrato.disconnect(); vibDepth.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_dance": {
        this.sfxNodeCount++;
        // Rhythmic kick: sine 80Hz burst every 250ms for 4 beats
        for (let i = 0; i < 4; i++) {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = 80;
          const env = ctx.createGain();
          const t = now + i * 0.25;
          env.gain.setValueAtTime(0.03, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 3) this.sfxNodeCount--; });
        }
        break;
      }

      case "emote_groove": {
        this.sfxNodeCount++;
        // Like dance but alternating 80Hz/120Hz
        for (let i = 0; i < 4; i++) {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = i % 2 === 0 ? 80 : 120;
          const env = ctx.createGain();
          const t = now + i * 0.25;
          env.gain.setValueAtTime(0.025, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.07);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 3) this.sfxNodeCount--; });
        }
        break;
      }

      case "emote_spin": {
        this.sfxNodeCount++;
        // Rising filtered noise sweep: BPF 500→2000Hz over 0.3s
        const src = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 500, 1.5);
        bpf.frequency.setValueAtTime(500, now);
        bpf.frequency.exponentialRampToValueAtTime(2000, now + 0.3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        src.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.36);
        src.addEventListener("ended", () => { src.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_air_guitar": {
        this.sfxNodeCount++;
        // Fast descending sweep 1200→200Hz over 0.15s — the "wah"
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(1200, now);
        osc.frequency.exponentialRampToValueAtTime(200, now + 0.15);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.025, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.21);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_think": {
        this.sfxNodeCount++;
        // Single soft tone at 440Hz, 0.3s decay
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 440;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.01, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.31);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "emote_shrug": {
        this.sfxNodeCount++;
        // Two-note minor second: E4 (329.63Hz) → Eb4 (311.13Hz)
        [329.63, 311.13].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          const t = now + i * 0.1;
          env.gain.setValueAtTime(0.015, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
          osc.connect(env); env.connect(sfxGain);
          osc.start(t); osc.stop(t + 0.13);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (i === 1) this.sfxNodeCount--; });
        });
        break;
      }

      case "emote_flex": {
        this.sfxNodeCount++;
        // Low power chord: E2 (82.41Hz) + B2 (123.47Hz)
        [82.41, 123.47].forEach((freq) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const env = ctx.createGain();
          env.gain.setValueAtTime(0.02, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 0.21);
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); if (freq > 100) this.sfxNodeCount--; });
        });
        break;
      }

      case "emote_sleep": {
        this.sfxNodeCount++;
        // Very quiet sine at 220Hz with slow 0.5Hz AM, long 1s decay
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 220;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.005;

        const amLFO = ctx.createOscillator();
        amLFO.type = "sine";
        amLFO.frequency.value = 0.5;
        const amDepth = ctx.createGain();
        amDepth.gain.value = 0.004;
        amLFO.connect(amDepth);
        amDepth.connect(oscGain.gain);

        const env = ctx.createGain();
        env.gain.setValueAtTime(1, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
        osc.connect(oscGain); oscGain.connect(env); env.connect(sfxGain);
        osc.start(now); amLFO.start(now);
        osc.stop(now + 1.01); amLFO.stop(now + 1.01);
        osc.addEventListener("ended", () => { osc.disconnect(); oscGain.disconnect(); env.disconnect(); amLFO.disconnect(); amDepth.disconnect(); this.sfxNodeCount--; });
        break;
      }

      // ── Pet Interaction Sounds ──────────────────────────────────────────

      case "pet_pat": {
        this.sfxNodeCount++;
        // Soft brown noise thud: LPF 300Hz, 0.03s
        const src = this._brownSource(ctx);
        const lpf = createLPF(ctx, 300, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.02, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        src.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.035);
        src.addEventListener("ended", () => { src.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "pet_eat": {
        this.sfxNodeCount++;
        // Rapid clicking: 3 white noise bursts through BPF 3000Hz, 60ms apart
        for (let i = 0; i < 3; i++) {
          const src = this._whiteSource(ctx);
          const bpf = createBPF(ctx, 3000, 4);
          const env = ctx.createGain();
          const t = now + i * 0.06;
          env.gain.setValueAtTime(0.015, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
          src.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          src.start(t); src.stop(t + 0.025);
          src.addEventListener("ended", () => { src.disconnect(); bpf.disconnect(); env.disconnect(); if (i === 2) this.sfxNodeCount--; });
        }
        break;
      }

      case "pet_play": {
        this.sfxNodeCount++;
        // Bright ascending chime: C5→G5 sine sweep 0.1s + noise burst
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.exponentialRampToValueAtTime(783.99, now + 0.1);
        const oscEnv = ctx.createGain();
        oscEnv.gain.setValueAtTime(0.02, now);
        oscEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        osc.connect(oscEnv); oscEnv.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.16);
        osc.addEventListener("ended", () => { osc.disconnect(); oscEnv.disconnect(); });

        const burst = this._whiteSource(ctx);
        const burstBPF = createBPF(ctx, 2000, 2);
        const burstEnv = ctx.createGain();
        burstEnv.gain.setValueAtTime(0.015, now + 0.05);
        burstEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        burst.connect(burstBPF); burstBPF.connect(burstEnv); burstEnv.connect(sfxGain);
        burst.start(now + 0.05); burst.stop(now + 0.11);
        burst.addEventListener("ended", () => { burst.disconnect(); burstBPF.disconnect(); burstEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "pet_groom": {
        this.sfxNodeCount++;
        // Soft continuous noise through HPF 4000Hz LPF 8000Hz, 0.3s with slow fade
        const src = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 4000, 0.7);
        const lpf = createLPF(ctx, 8000, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.01, now);
        env.gain.setValueAtTime(0.01, now + 0.15);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        src.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.31);
        src.addEventListener("ended", () => { src.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      // ── Furniture Interaction Sounds ──────────────────────────────────

      case "toggle_on": {
        this.sfxNodeCount++;
        // Click + warm ascending tone — something coming alive
        const click = this._whiteSource(ctx);
        const clickBPF = createBPF(ctx, 2500, 5);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.05, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.008);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.01);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); });
        // Warm rising tone
        const tone = ctx.createOscillator();
        tone.type = "sine";
        tone.frequency.setValueAtTime(250, now + 0.01);
        tone.frequency.exponentialRampToValueAtTime(400, now + 0.08);
        const toneEnv = ctx.createGain();
        toneEnv.gain.setValueAtTime(0.035, now + 0.01);
        toneEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        tone.connect(toneEnv); toneEnv.connect(sfxGain);
        tone.start(now + 0.01); tone.stop(now + 0.13);
        tone.addEventListener("ended", () => { tone.disconnect(); toneEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "toggle_off": {
        this.sfxNodeCount++;
        // Click + descending tone — something winding down
        const click = this._whiteSource(ctx);
        const clickBPF = createBPF(ctx, 2000, 5);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.04, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.008);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.01);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); });
        // Descending tone
        const tone = ctx.createOscillator();
        tone.type = "sine";
        tone.frequency.setValueAtTime(350, now + 0.01);
        tone.frequency.exponentialRampToValueAtTime(200, now + 0.1);
        const toneEnv = ctx.createGain();
        toneEnv.gain.setValueAtTime(0.03, now + 0.01);
        toneEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        tone.connect(toneEnv); toneEnv.connect(sfxGain);
        tone.start(now + 0.01); tone.stop(now + 0.13);
        tone.addEventListener("ended", () => { tone.disconnect(); toneEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "lamp_switch": {
        this.sfxNodeCount++;
        // Crisp mechanical click with faint glass resonance
        const click = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 3500, 6);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.06, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.006);
        click.connect(bpf); bpf.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.008);
        click.addEventListener("ended", () => { click.disconnect(); bpf.disconnect(); clickEnv.disconnect(); });
        // Glass resonance
        const glass = ctx.createOscillator();
        glass.type = "sine"; glass.frequency.value = 2800;
        const glassEnv = ctx.createGain();
        glassEnv.gain.setValueAtTime(0.008, now + 0.005);
        glassEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        glass.connect(glassEnv); glassEnv.connect(sfxGain);
        glass.start(now + 0.005); glass.stop(now + 0.07);
        glass.addEventListener("ended", () => { glass.disconnect(); glassEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "chest_open": {
        this.sfxNodeCount++;
        // Wooden creak: brown noise through mid BPF with slow attack
        const creak = this._brownSource(ctx);
        const bpf = createBPF(ctx, 700, 2);
        const creakEnv = ctx.createGain();
        creakEnv.gain.setValueAtTime(0, now);
        creakEnv.gain.linearRampToValueAtTime(0.04, now + 0.04);
        creakEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        creak.connect(bpf); bpf.connect(creakEnv); creakEnv.connect(sfxGain);
        creak.start(now); creak.stop(now + 0.22);
        creak.addEventListener("ended", () => { creak.disconnect(); bpf.disconnect(); creakEnv.disconnect(); this.sfxNodeCount--; });
        // Metal hinge squeak
        const hinge = ctx.createOscillator();
        hinge.type = "sine";
        hinge.frequency.setValueAtTime(1800, now + 0.02);
        hinge.frequency.exponentialRampToValueAtTime(1200, now + 0.12);
        const hingeEnv = ctx.createGain();
        hingeEnv.gain.setValueAtTime(0.012, now + 0.02);
        hingeEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        hinge.connect(hingeEnv); hingeEnv.connect(sfxGain);
        hinge.start(now + 0.02); hinge.stop(now + 0.13);
        hinge.addEventListener("ended", () => { hinge.disconnect(); hingeEnv.disconnect(); });
        break;
      }

      case "chest_close": {
        this.sfxNodeCount++;
        // Thud + latch click
        const thud = this._brownSource(ctx);
        const lpf = createLPF(ctx, 300, 0.8);
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.04, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        thud.connect(lpf); lpf.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.05);
        thud.addEventListener("ended", () => { thud.disconnect(); lpf.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        // Latch click
        const latch = this._whiteSource(ctx);
        const latchBPF = createBPF(ctx, 3000, 6);
        const latchEnv = ctx.createGain();
        latchEnv.gain.setValueAtTime(0.04, now + 0.03);
        latchEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        latch.connect(latchBPF); latchBPF.connect(latchEnv); latchEnv.connect(sfxGain);
        latch.start(now + 0.03); latch.stop(now + 0.045);
        latch.addEventListener("ended", () => { latch.disconnect(); latchBPF.disconnect(); latchEnv.disconnect(); });
        break;
      }

      case "bookshelf_rustle": {
        this.sfxNodeCount++;
        // Paper rustle: high-frequency filtered white noise, gentle
        const src = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 2500, 0.7);
        const lpf = createLPF(ctx, 7000, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.025, now + 0.03);
        env.gain.setValueAtTime(0.025, now + 0.1);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        src.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.22);
        src.addEventListener("ended", () => { src.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "door_open": {
        this.sfxNodeCount++;
        // Wooden door: creak sweep up + air whoosh
        const creak = this._brownSource(ctx);
        const bpf = createBPF(ctx, 500, 2);
        bpf.frequency.setValueAtTime(400, now);
        bpf.frequency.exponentialRampToValueAtTime(800, now + 0.15);
        const creakEnv = ctx.createGain();
        creakEnv.gain.setValueAtTime(0.03, now);
        creakEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        creak.connect(bpf); bpf.connect(creakEnv); creakEnv.connect(sfxGain);
        creak.start(now); creak.stop(now + 0.2);
        creak.addEventListener("ended", () => { creak.disconnect(); bpf.disconnect(); creakEnv.disconnect(); this.sfxNodeCount--; });
        // Air displacement
        const air = this._pinkSource(ctx);
        const airLPF = createLPF(ctx, 1000, 0.5);
        const airEnv = ctx.createGain();
        airEnv.gain.setValueAtTime(0.015, now + 0.05);
        airEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        air.connect(airLPF); airLPF.connect(airEnv); airEnv.connect(sfxGain);
        air.start(now + 0.05); air.stop(now + 0.16);
        air.addEventListener("ended", () => { air.disconnect(); airLPF.disconnect(); airEnv.disconnect(); });
        break;
      }

      case "door_close": {
        this.sfxNodeCount++;
        // Thud + latch — heavier than chest
        const thud = this._brownSource(ctx);
        const lpf = createLPF(ctx, 250, 0.7);
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.05, now);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        thud.connect(lpf); lpf.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.07);
        thud.addEventListener("ended", () => { thud.disconnect(); lpf.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "fire_ignite": {
        this.sfxNodeCount++;
        // Whoosh + crackle burst — the satisfying moment of lighting a fire
        const whoosh = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 800, 1.5);
        bpf.frequency.setValueAtTime(400, now);
        bpf.frequency.exponentialRampToValueAtTime(2000, now + 0.15);
        const whooshEnv = ctx.createGain();
        whooshEnv.gain.setValueAtTime(0.04, now);
        whooshEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        whoosh.connect(bpf); bpf.connect(whooshEnv); whooshEnv.connect(sfxGain);
        whoosh.start(now); whoosh.stop(now + 0.22);
        whoosh.addEventListener("ended", () => { whoosh.disconnect(); bpf.disconnect(); whooshEnv.disconnect(); this.sfxNodeCount--; });
        // Low flame thump
        const flame = ctx.createOscillator();
        flame.type = "sine"; flame.frequency.value = 100;
        const flameEnv = ctx.createGain();
        flameEnv.gain.setValueAtTime(0.03, now + 0.05);
        flameEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        flame.connect(flameEnv); flameEnv.connect(sfxGain);
        flame.start(now + 0.05); flame.stop(now + 0.13);
        flame.addEventListener("ended", () => { flame.disconnect(); flameEnv.disconnect(); });
        break;
      }

      case "needle_drop": {
        this.sfxNodeCount++;
        // Record needle: tiny click + faint hiss onset — the anticipation
        const click = this._whiteSource(ctx);
        const clickBPF = createBPF(ctx, 4000, 8);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(0.04, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.005);
        click.connect(clickBPF); clickBPF.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.008);
        click.addEventListener("ended", () => { click.disconnect(); clickBPF.disconnect(); clickEnv.disconnect(); });
        // Vinyl surface hiss rising in
        const hiss = this._pinkSource(ctx);
        const hissHPF = createHPF(ctx, 4000, 1);
        const hissEnv = ctx.createGain();
        hissEnv.gain.setValueAtTime(0, now + 0.01);
        hissEnv.gain.linearRampToValueAtTime(0.008, now + 0.15);
        hissEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
        hiss.connect(hissHPF); hissHPF.connect(hissEnv); hissEnv.connect(sfxGain);
        hiss.start(now + 0.01); hiss.stop(now + 0.42);
        hiss.addEventListener("ended", () => { hiss.disconnect(); hissHPF.disconnect(); hissEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "place_wood": {
        this.sfxNodeCount++;
        // Warm thud — setting something wooden down
        const thud = this._brownSource(ctx);
        const bpf = createBPF(ctx, 350, 3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.04, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        thud.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        thud.start(now); thud.stop(now + 0.05);
        thud.addEventListener("ended", () => { thud.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "place_stone": {
        this.sfxNodeCount++;
        // Heavy scrape + thud
        const scrape = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 900, 3);
        const scrapeEnv = ctx.createGain();
        scrapeEnv.gain.setValueAtTime(0.025, now);
        scrapeEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        scrape.connect(bpf); bpf.connect(scrapeEnv); scrapeEnv.connect(sfxGain);
        scrape.start(now); scrape.stop(now + 0.05);
        scrape.addEventListener("ended", () => { scrape.disconnect(); bpf.disconnect(); scrapeEnv.disconnect(); });
        // Low thud
        const thud = ctx.createOscillator();
        thud.type = "sine"; thud.frequency.value = 80;
        const thudEnv = ctx.createGain();
        thudEnv.gain.setValueAtTime(0.04, now + 0.02);
        thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        thud.connect(thudEnv); thudEnv.connect(sfxGain);
        thud.start(now + 0.02); thud.stop(now + 0.07);
        thud.addEventListener("ended", () => { thud.disconnect(); thudEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "place_metal": {
        this.sfxNodeCount++;
        // Metallic clank + resonance
        const clank = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 2000, 2);
        const clankEnv = ctx.createGain();
        clankEnv.gain.setValueAtTime(0.05, now);
        clankEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        clank.connect(hpf); hpf.connect(clankEnv); clankEnv.connect(sfxGain);
        clank.start(now); clank.stop(now + 0.025);
        clank.addEventListener("ended", () => { clank.disconnect(); hpf.disconnect(); clankEnv.disconnect(); });
        // Ring
        const ring = ctx.createOscillator();
        ring.type = "sine"; ring.frequency.value = 1400;
        const ringEnv = ctx.createGain();
        ringEnv.gain.setValueAtTime(0.03, now);
        ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        ring.connect(ringEnv); ringEnv.connect(sfxGain);
        ring.start(now); ring.stop(now + 0.13);
        ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "water_pour": {
        this.sfxNodeCount++;
        // Trickling: filtered white noise with modulated BPF
        const src = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 2000, 1.5);
        const lfo = ctx.createOscillator();
        lfo.type = "sine"; lfo.frequency.value = 6;
        const lfoDepth = ctx.createGain();
        lfoDepth.gain.value = 500;
        lfo.connect(lfoDepth); lfoDepth.connect(bpf.frequency);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.03, now + 0.05);
        env.gain.setValueAtTime(0.03, now + 0.25);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
        src.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        src.start(now); lfo.start(now);
        src.stop(now + 0.42); lfo.stop(now + 0.42);
        src.addEventListener("ended", () => { src.disconnect(); bpf.disconnect(); lfo.disconnect(); lfoDepth.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "clock_wind": {
        this.sfxNodeCount++;
        // Rhythmic ticking: two quick clicks
        [0, 0.12].forEach((delay) => {
          const click = this._whiteSource(ctx);
          const bpf = createBPF(ctx, 2500, 8);
          const env = ctx.createGain();
          const t = now + delay;
          env.gain.setValueAtTime(0.025, t);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.005);
          click.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          click.start(t); click.stop(t + 0.008);
          click.addEventListener("ended", () => { click.disconnect(); bpf.disconnect(); env.disconnect(); if (delay > 0) this.sfxNodeCount--; });
        });
        break;
      }

      case "cloth_rustle": {
        this.sfxNodeCount++;
        // Fabric movement: very soft high noise
        const src = this._pinkSource(ctx);
        const hpf = createHPF(ctx, 2000, 0.5);
        const lpf = createLPF(ctx, 6000, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.015, now + 0.02);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        src.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.12);
        src.addEventListener("ended", () => { src.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "chair_creak": {
        this.sfxNodeCount++;
        // Wood stress creak — the sound of weight shifting onto old wood
        const creak = this._brownSource(ctx);
        const bpf = createBPF(ctx, 600, 3);
        bpf.frequency.setValueAtTime(400, now);
        bpf.frequency.exponentialRampToValueAtTime(900, now + 0.08);
        const creakEnv = ctx.createGain();
        creakEnv.gain.setValueAtTime(0, now);
        creakEnv.gain.linearRampToValueAtTime(0.03, now + 0.02);
        creakEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        creak.connect(bpf); bpf.connect(creakEnv); creakEnv.connect(sfxGain);
        creak.start(now); creak.stop(now + 0.14);
        creak.addEventListener("ended", () => { creak.disconnect(); bpf.disconnect(); creakEnv.disconnect(); this.sfxNodeCount--; });
        // Subtle wood knock underneath
        const knock = ctx.createOscillator();
        knock.type = "sine"; knock.frequency.value = 180;
        const knockEnv = ctx.createGain();
        knockEnv.gain.setValueAtTime(0.02, now);
        knockEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);
        knock.connect(knockEnv); knockEnv.connect(sfxGain);
        knock.start(now); knock.stop(now + 0.04);
        knock.addEventListener("ended", () => { knock.disconnect(); knockEnv.disconnect(); });
        break;
      }

      case "cloth_settle": {
        this.sfxNodeCount++;
        // Fabric compression — slow, soft, like sinking into bedding
        const src = this._pinkSource(ctx);
        const lpf = createLPF(ctx, 3000, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.012, now + 0.06);
        env.gain.setValueAtTime(0.012, now + 0.15);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        src.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.32);
        src.addEventListener("ended", () => { src.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // Faint spring/frame creak
        const spring = ctx.createOscillator();
        spring.type = "sine";
        spring.frequency.setValueAtTime(320, now + 0.05);
        spring.frequency.exponentialRampToValueAtTime(220, now + 0.15);
        const springEnv = ctx.createGain();
        springEnv.gain.setValueAtTime(0.008, now + 0.05);
        springEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        spring.connect(springEnv); springEnv.connect(sfxGain);
        spring.start(now + 0.05); spring.stop(now + 0.19);
        spring.addEventListener("ended", () => { spring.disconnect(); springEnv.disconnect(); });
        break;
      }

      case "glass_tap": {
        this.sfxNodeCount++;
        // Delicate finger tap on glass — bright, thin, resonant
        const tap = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 3500, 8);
        const tapEnv = ctx.createGain();
        tapEnv.gain.setValueAtTime(0.035, now);
        tapEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.006);
        tap.connect(bpf); bpf.connect(tapEnv); tapEnv.connect(sfxGain);
        tap.start(now); tap.stop(now + 0.008);
        tap.addEventListener("ended", () => { tap.disconnect(); bpf.disconnect(); tapEnv.disconnect(); });
        // Glass resonance — two harmonics
        const res1 = ctx.createOscillator();
        res1.type = "sine"; res1.frequency.value = 2200;
        const res1Env = ctx.createGain();
        res1Env.gain.setValueAtTime(0.015, now);
        res1Env.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        res1.connect(res1Env); res1Env.connect(sfxGain);
        res1.start(now); res1.stop(now + 0.16);
        const res2 = ctx.createOscillator();
        res2.type = "sine"; res2.frequency.value = 3600;
        const res2Env = ctx.createGain();
        res2Env.gain.setValueAtTime(0.008, now);
        res2Env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        res2.connect(res2Env); res2Env.connect(sfxGain);
        res2.start(now); res2.stop(now + 0.11);
        res1.addEventListener("ended", () => { res1.disconnect(); res1Env.disconnect(); this.sfxNodeCount--; });
        res2.addEventListener("ended", () => { res2.disconnect(); res2Env.disconnect(); });
        break;
      }

      case "arcade_coin": {
        this.sfxNodeCount++;
        // Coin dropping into metal slot — clink + rattling descent
        const clink = this._whiteSource(ctx);
        const clinkBPF = createBPF(ctx, 5000, 6);
        const clinkEnv = ctx.createGain();
        clinkEnv.gain.setValueAtTime(0.05, now);
        clinkEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        clink.connect(clinkBPF); clinkBPF.connect(clinkEnv); clinkEnv.connect(sfxGain);
        clink.start(now); clink.stop(now + 0.02);
        clink.addEventListener("ended", () => { clink.disconnect(); clinkBPF.disconnect(); clinkEnv.disconnect(); });
        // Coin rattle — descending metallic ring
        const ring = ctx.createOscillator();
        ring.type = "triangle";
        ring.frequency.setValueAtTime(3200, now + 0.02);
        ring.frequency.exponentialRampToValueAtTime(1800, now + 0.1);
        const ringEnv = ctx.createGain();
        ringEnv.gain.setValueAtTime(0.025, now + 0.02);
        ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        ring.connect(ringEnv); ringEnv.connect(sfxGain);
        ring.start(now + 0.02); ring.stop(now + 0.13);
        ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); });
        // Mechanism thunk at the bottom
        const thunk = ctx.createOscillator();
        thunk.type = "sine"; thunk.frequency.value = 200;
        const thunkEnv = ctx.createGain();
        thunkEnv.gain.setValueAtTime(0.02, now + 0.1);
        thunkEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        thunk.connect(thunkEnv); thunkEnv.connect(sfxGain);
        thunk.start(now + 0.1); thunk.stop(now + 0.15);
        thunk.addEventListener("ended", () => { thunk.disconnect(); thunkEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "chess_piece": {
        this.sfxNodeCount++;
        // Small wooden piece set on wooden board — clean, precise, satisfying
        const tap = this._brownSource(ctx);
        const bpf = createBPF(ctx, 1200, 5);
        const tapEnv = ctx.createGain();
        tapEnv.gain.setValueAtTime(0.035, now);
        tapEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        tap.connect(bpf); bpf.connect(tapEnv); tapEnv.connect(sfxGain);
        tap.start(now); tap.stop(now + 0.025);
        tap.addEventListener("ended", () => { tap.disconnect(); bpf.disconnect(); tapEnv.disconnect(); });
        // Board resonance — hollow wood body
        const body = ctx.createOscillator();
        body.type = "sine"; body.frequency.value = 280;
        const bodyEnv = ctx.createGain();
        bodyEnv.gain.setValueAtTime(0.015, now);
        bodyEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        body.connect(bodyEnv); bodyEnv.connect(sfxGain);
        body.start(now); body.stop(now + 0.07);
        body.addEventListener("ended", () => { body.disconnect(); bodyEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "crystal_hum": {
        this.sfxNodeCount++;
        // Ethereal resonance — two detuned sines with slow shimmer
        const osc1 = ctx.createOscillator();
        osc1.type = "sine"; osc1.frequency.value = 520;
        const osc2 = ctx.createOscillator();
        osc2.type = "sine"; osc2.frequency.value = 526; // 6Hz beat frequency
        const env1 = ctx.createGain();
        env1.gain.setValueAtTime(0, now);
        env1.gain.linearRampToValueAtTime(0.02, now + 0.1);
        env1.gain.setValueAtTime(0.02, now + 0.3);
        env1.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
        const env2 = ctx.createGain();
        env2.gain.setValueAtTime(0, now);
        env2.gain.linearRampToValueAtTime(0.015, now + 0.1);
        env2.gain.setValueAtTime(0.015, now + 0.3);
        env2.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
        osc1.connect(env1); env1.connect(sfxGain);
        osc2.connect(env2); env2.connect(sfxGain);
        osc1.start(now); osc2.start(now);
        osc1.stop(now + 0.62); osc2.stop(now + 0.62);
        osc1.addEventListener("ended", () => { osc1.disconnect(); env1.disconnect(); this.sfxNodeCount--; });
        osc2.addEventListener("ended", () => { osc2.disconnect(); env2.disconnect(); });
        // High harmonic shimmer
        const shimmer = ctx.createOscillator();
        shimmer.type = "sine"; shimmer.frequency.value = 1560;
        const shimEnv = ctx.createGain();
        shimEnv.gain.setValueAtTime(0, now);
        shimEnv.gain.linearRampToValueAtTime(0.006, now + 0.15);
        shimEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        shimmer.connect(shimEnv); shimEnv.connect(sfxGain);
        shimmer.start(now); shimmer.stop(now + 0.52);
        shimmer.addEventListener("ended", () => { shimmer.disconnect(); shimEnv.disconnect(); });
        break;
      }

      case "water_drip": {
        this.sfxNodeCount++;
        // Single water drop — high-pitched plip with tiny resonance
        const plip = ctx.createOscillator();
        plip.type = "sine";
        plip.frequency.setValueAtTime(1800, now);
        plip.frequency.exponentialRampToValueAtTime(1200, now + 0.03);
        const plipEnv = ctx.createGain();
        plipEnv.gain.setValueAtTime(0.025, now);
        plipEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        plip.connect(plipEnv); plipEnv.connect(sfxGain);
        plip.start(now); plip.stop(now + 0.07);
        plip.addEventListener("ended", () => { plip.disconnect(); plipEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }

      case "generic":
      default: {
        this.sfxNodeCount++;
        // Subtle ping
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 800;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.11);
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
    }
  }

  // ─── Weapon-Aware Combat Sounds ──────────────────────────────────────────────

  /**
   * Synthesize a weapon-class-aware swing whoosh.
   * Heavier weapons = deeper pitch, longer duration.
   */
  private _playCombatSwing(weaponClass: string): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;

    // Weapon weight maps to whoosh character
    // Light weapons: high pitch, short. Heavy: low pitch, long.
    let freqStart = 1200, freqEnd = 3000, dur = 0.1, vol = 0.03;
    switch (weaponClass) {
      case "barehand":
        freqStart = 800; freqEnd = 1500; dur = 0.06; vol = 0.015; break;
      case "dagger":
        freqStart = 1800; freqEnd = 4000; dur = 0.06; vol = 0.025; break;
      case "sword_1h":
        freqStart = 1200; freqEnd = 3200; dur = 0.1; vol = 0.03; break;
      case "sword_2h":
        freqStart = 800; freqEnd = 2400; dur = 0.15; vol = 0.035; break;
      case "axe":
        freqStart = 600; freqEnd = 1800; dur = 0.14; vol = 0.04; break;
      case "mace":
        freqStart = 500; freqEnd = 1400; dur = 0.16; vol = 0.04; break;
      case "staff":
        freqStart = 900; freqEnd = 2200; dur = 0.12; vol = 0.03; break;
    }

    const whoosh = this._whiteSource(ctx);
    const bpf = createBPF(ctx, freqStart, 2);
    bpf.frequency.setValueAtTime(freqStart, now);
    bpf.frequency.exponentialRampToValueAtTime(freqEnd, now + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(vol, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + dur + 0.02);
    whoosh.connect(bpf); bpf.connect(env); env.connect(sfxGain);
    whoosh.start(now); whoosh.stop(now + dur + 0.04);
    this.sfxNodeCount++;
    whoosh.addEventListener("ended", () => { whoosh.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
  }

  /**
   * Synthesize a weapon-class + target-material combat impact.
   *
   * The sound is built from two layers:
   * 1. Weapon attack tone (what's doing the hitting)
   * 2. Target reaction tone (what's being hit)
   * Plus optional crit metallic ring.
   */
  private _playCombatHit(weaponClass: string, targetMaterial: string, isCrit: boolean, intensity: number): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES - 2) return;

    const now = ctx.currentTime;
    const vol = 0.03 + intensity * 0.04;

    // ── Layer 1: Weapon attack character ────────────────────────────────
    switch (weaponClass) {
      case "barehand": {
        // Fleshy thump: LPF brown noise + deep sine
        const thump = this._brownSource(ctx);
        const lpf = createLPF(ctx, 500, 1.0);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        thump.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        thump.start(now); thump.stop(now + 0.1);
        this.sfxNodeCount++;
        thump.addEventListener("ended", () => { thump.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "dagger": {
        // Quick, sharp stab: HPF white noise click + thin sine 1800Hz
        const stab = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 2500, 3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.8, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
        stab.connect(hpf); hpf.connect(env); env.connect(sfxGain);
        stab.start(now); stab.stop(now + 0.04);
        this.sfxNodeCount++;
        stab.addEventListener("ended", () => { stab.disconnect(); hpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "sword_1h":
      case "sword_2h": {
        // Metal blade ring: sine harmonics + noise transient
        const blade = ctx.createOscillator();
        blade.type = "triangle";
        const ringFreq = weaponClass === "sword_2h" ? 800 : 1100;
        blade.frequency.setValueAtTime(ringFreq, now);
        blade.frequency.exponentialRampToValueAtTime(ringFreq * 0.6, now + 0.15);
        const bladeEnv = ctx.createGain();
        bladeEnv.gain.setValueAtTime(vol * 0.7, now);
        bladeEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        blade.connect(bladeEnv); bladeEnv.connect(sfxGain);
        blade.start(now); blade.stop(now + 0.2);
        this.sfxNodeCount++;
        blade.addEventListener("ended", () => { blade.disconnect(); bladeEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "axe": {
        // Heavy chop: low BPF brown noise + deep body sine
        const chop = this._brownSource(ctx);
        const bpf = createBPF(ctx, 400, 2);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 1.2, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        chop.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        chop.start(now); chop.stop(now + 0.12);
        this.sfxNodeCount++;
        chop.addEventListener("ended", () => { chop.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "mace": {
        // Crushing impact: wide noise burst + very low sine
        const crush = this._whiteSource(ctx);
        const lpf = createLPF(ctx, 1200, 0.8);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 1.3, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
        crush.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        crush.start(now); crush.stop(now + 0.14);
        this.sfxNodeCount++;
        crush.addEventListener("ended", () => { crush.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // Sub-bass body
        const sub = ctx.createOscillator();
        sub.type = "sine"; sub.frequency.value = 55;
        const subEnv = ctx.createGain();
        subEnv.gain.setValueAtTime(vol * 0.8, now);
        subEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        sub.connect(subEnv); subEnv.connect(sfxGain);
        sub.start(now); sub.stop(now + 0.12);
        this.sfxNodeCount++;
        sub.addEventListener("ended", () => { sub.disconnect(); subEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "staff": {
        // Wooden crack: BPF brown noise at 800Hz + wood body
        const crack = this._brownSource(ctx);
        const bpf = createBPF(ctx, 800, 3);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.9, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        crack.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        crack.start(now); crack.stop(now + 0.1);
        this.sfxNodeCount++;
        crack.addEventListener("ended", () => { crack.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      default: {
        // Generic hit: noise burst + thud (same as old combat_hit)
        const noise = this._whiteSource(ctx);
        const lpf = createLPF(ctx, 800);
        const noiseEnv = ctx.createGain();
        noiseEnv.gain.setValueAtTime(vol, now);
        noiseEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        noise.connect(lpf); lpf.connect(noiseEnv); noiseEnv.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.12);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); lpf.disconnect(); noiseEnv.disconnect(); this.sfxNodeCount--; });
      }
    }

    // ── Layer 2: Target material reaction ────────────────────────────────
    // This colors the impact with what's BEING hit
    let targetFreq = 200, targetQ = 2, targetDur = 0.06, targetVol = vol * 0.5;
    switch (targetMaterial) {
      case "organic":
        targetFreq = 150; targetQ = 1; targetDur = 0.05; targetVol = vol * 0.3; break;
      case "wood":
        targetFreq = 300; targetQ = 3; targetDur = 0.08; targetVol = vol * 0.5; break;
      case "metal":
        targetFreq = 1200; targetQ = 5; targetDur = 0.12; targetVol = vol * 0.4; break;
      case "stone":
        targetFreq = 500; targetQ = 2; targetDur = 0.06; targetVol = vol * 0.6; break;
    }

    const targetSrc = this._brownSource(ctx);
    const targetBPF = createBPF(ctx, targetFreq, targetQ);
    const targetEnv = ctx.createGain();
    targetEnv.gain.setValueAtTime(targetVol, now);
    targetEnv.gain.exponentialRampToValueAtTime(0.0001, now + targetDur);
    targetSrc.connect(targetBPF); targetBPF.connect(targetEnv); targetEnv.connect(sfxGain);
    targetSrc.start(now); targetSrc.stop(now + targetDur + 0.02);
    this.sfxNodeCount++;
    targetSrc.addEventListener("ended", () => { targetSrc.disconnect(); targetBPF.disconnect(); targetEnv.disconnect(); this.sfxNodeCount--; });

    // Body thud — always present, gives weight to any hit
    const body = ctx.createOscillator();
    body.type = "sine";
    body.frequency.setValueAtTime(80, now);
    body.frequency.exponentialRampToValueAtTime(50, now + 0.08);
    const bodyEnv = ctx.createGain();
    bodyEnv.gain.setValueAtTime(vol * 0.6, now);
    bodyEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    body.connect(bodyEnv); bodyEnv.connect(sfxGain);
    body.start(now); body.stop(now + 0.12);
    this.sfxNodeCount++;
    body.addEventListener("ended", () => { body.disconnect(); bodyEnv.disconnect(); this.sfxNodeCount--; });

    // ── Layer 3: Crit ring (optional) ────────────────────────────────────
    if (isCrit) {
      const ring = ctx.createOscillator();
      ring.type = "sine";
      ring.frequency.value = 1400;
      const ringEnv = ctx.createGain();
      ringEnv.gain.setValueAtTime(0.04, now);
      ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
      ring.connect(ringEnv); ringEnv.connect(sfxGain);
      ring.start(now); ring.stop(now + 0.22);
      this.sfxNodeCount++;
      ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); this.sfxNodeCount--; });
    }
  }

  // ─── Creature Vocalizations ──────────────────────────────────────────────────

  /**
   * Play a one-shot creature vocalization.
   */
  private _playCreatureVocal(type: string, pitch: number): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    const ambientGain = this.ambientGain;
    if (!ctx || !sfxGain || !ambientGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;

    switch (type) {

      case "meow": {
        // Sine sweep 800→400Hz with gentle vibrato — a cat's greeting
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(800 * pitch, now);
        osc.frequency.linearRampToValueAtTime(400 * pitch, now + 0.15);

        // 5Hz vibrato, depth 30Hz
        const vibrato = ctx.createOscillator();
        vibrato.type = "sine";
        vibrato.frequency.value = 5;
        const vibratoGain = ctx.createGain();
        vibratoGain.gain.value = 30 * pitch;
        vibrato.connect(vibratoGain);
        vibratoGain.connect(osc.frequency);

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.04, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);

        osc.connect(env);
        env.connect(sfxGain);
        osc.start(now);
        vibrato.start(now);
        osc.stop(now + 0.31);
        vibrato.stop(now + 0.31);
        this.sfxNodeCount += 2;

        osc.addEventListener("ended", () => {
          osc.disconnect(); env.disconnect();
          vibrato.disconnect(); vibratoGain.disconnect();
          this.sfxNodeCount -= 2;
        });
        break;
      }

      case "bark": {
        // Noise burst through BPF — sharp, percussive double-bark
        const source = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 600 * pitch, 4);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.05, now + 0.001); // 1ms attack
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

        source.connect(bpf);
        bpf.connect(env);
        env.connect(sfxGain);
        source.start(now);
        source.stop(now + 0.07);
        this.sfxNodeCount++;

        source.addEventListener("ended", () => {
          source.disconnect(); bpf.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });

        // Second softer burst 30ms later (the "double bark" feel)
        if (this.sfxNodeCount < MAX_SFX_NODES) {
          const source2 = this._whiteSource(ctx);
          const bpf2 = createBPF(ctx, 600 * pitch, 4);
          const env2 = ctx.createGain();
          const t2 = now + 0.03;
          env2.gain.setValueAtTime(0, t2);
          env2.gain.linearRampToValueAtTime(0.035, t2 + 0.001); // softer
          env2.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.06);

          source2.connect(bpf2);
          bpf2.connect(env2);
          env2.connect(sfxGain);
          source2.start(t2);
          source2.stop(t2 + 0.07);
          this.sfxNodeCount++;

          source2.addEventListener("ended", () => {
            source2.disconnect(); bpf2.disconnect(); env2.disconnect();
            this.sfxNodeCount--;
          });
        }
        break;
      }

      case "purr": {
        // 25Hz sine with 25Hz AM — characteristic rumble, routed to ambient bus
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 25;

        const lfo = ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = 25;

        const lfoGain = ctx.createGain();
        lfoGain.gain.value = 0.5; // AM depth

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.015, now);
        env.gain.setValueAtTime(0.015, now + 0.7);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);

        lfo.connect(lfoGain);
        lfoGain.connect(env.gain);
        osc.connect(env);
        env.connect(ambientGain); // ambient bus, not SFX
        osc.start(now);
        lfo.start(now);
        osc.stop(now + 0.81);
        lfo.stop(now + 0.81);
        this.sfxNodeCount += 2;

        osc.addEventListener("ended", () => {
          osc.disconnect(); lfo.disconnect(); lfoGain.disconnect(); env.disconnect();
          this.sfxNodeCount -= 2;
        });
        break;
      }

      case "neigh": {
        // Complex harmonic sweep with vibrato — majestic horse call
        const fundamental = ctx.createOscillator();
        fundamental.type = "sine";
        fundamental.frequency.setValueAtTime(200 * pitch, now);
        fundamental.frequency.linearRampToValueAtTime(350 * pitch, now + 0.4);

        const harm2 = ctx.createOscillator();
        harm2.type = "sine";
        harm2.frequency.setValueAtTime(400 * pitch, now);
        harm2.frequency.linearRampToValueAtTime(700 * pitch, now + 0.4);

        const harm3 = ctx.createOscillator();
        harm3.type = "sine";
        harm3.frequency.setValueAtTime(600 * pitch, now);
        harm3.frequency.linearRampToValueAtTime(1050 * pitch, now + 0.4);

        // 6Hz vibrato
        const vibrato = ctx.createOscillator();
        vibrato.type = "sine";
        vibrato.frequency.value = 6;
        const vibDepth = ctx.createGain();
        vibDepth.gain.value = 15 * pitch;
        vibrato.connect(vibDepth);
        vibDepth.connect(fundamental.frequency);
        vibDepth.connect(harm2.frequency);
        vibDepth.connect(harm3.frequency);

        const fundGain = ctx.createGain();
        fundGain.gain.setValueAtTime(0.04, now);
        fundGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

        const h2Gain = ctx.createGain();
        h2Gain.gain.setValueAtTime(0.025, now);
        h2Gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

        const h3Gain = ctx.createGain();
        h3Gain.gain.setValueAtTime(0.015, now);
        h3Gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

        fundamental.connect(fundGain); fundGain.connect(sfxGain);
        harm2.connect(h2Gain); h2Gain.connect(sfxGain);
        harm3.connect(h3Gain); h3Gain.connect(sfxGain);

        fundamental.start(now); harm2.start(now); harm3.start(now); vibrato.start(now);
        fundamental.stop(now + 0.51); harm2.stop(now + 0.51); harm3.stop(now + 0.51); vibrato.stop(now + 0.51);
        this.sfxNodeCount += 4;

        fundamental.addEventListener("ended", () => {
          fundamental.disconnect(); harm2.disconnect(); harm3.disconnect(); vibrato.disconnect();
          fundGain.disconnect(); h2Gain.disconnect(); h3Gain.disconnect(); vibDepth.disconnect();
          this.sfxNodeCount -= 4;
        });
        break;
      }

      case "howl": {
        // Long sine sweep up then back down — haunting wolf howl
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(300 * pitch, now);
        osc.frequency.linearRampToValueAtTime(500 * pitch, now + 0.4);
        osc.frequency.linearRampToValueAtTime(300 * pitch, now + 0.8);

        // Slight detune on second oscillator for haunting quality (+2 cents)
        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(300 * pitch, now);
        osc2.frequency.linearRampToValueAtTime(500 * pitch, now + 0.4);
        osc2.frequency.linearRampToValueAtTime(300 * pitch, now + 0.8);
        osc2.detune.value = 2;

        // 4Hz slow vibrato, depth 20Hz
        const vibrato = ctx.createOscillator();
        vibrato.type = "sine";
        vibrato.frequency.value = 4;
        const vibDepth = ctx.createGain();
        vibDepth.gain.value = 20 * pitch;
        vibrato.connect(vibDepth);
        vibDepth.connect(osc.frequency);
        vibDepth.connect(osc2.frequency);

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.setValueAtTime(0.03, now + 0.7);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);

        osc.connect(env);
        osc2.connect(env);
        env.connect(sfxGain);
        osc.start(now); osc2.start(now); vibrato.start(now);
        osc.stop(now + 0.81); osc2.stop(now + 0.81); vibrato.stop(now + 0.81);
        this.sfxNodeCount += 3;

        osc.addEventListener("ended", () => {
          osc.disconnect(); osc2.disconnect(); vibrato.disconnect();
          env.disconnect(); vibDepth.disconnect();
          this.sfxNodeCount -= 3;
        });
        break;
      }

      case "chirp": {
        // Very short sine sweep — bird-like, for phoenix/griffin
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(2000 * pitch, now);
        osc.frequency.linearRampToValueAtTime(1000 * pitch, now + 0.05);

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);

        osc.connect(env);
        env.connect(sfxGain);
        osc.start(now);
        osc.stop(now + 0.06);
        this.sfxNodeCount++;

        osc.addEventListener("ended", () => {
          osc.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });
        break;
      }

      case "growl": {
        // Brown noise through BPF with amplitude tremolo — deep and threatening
        const source = this._brownSource(ctx);
        const bpf = createBPF(ctx, 150 * pitch, 3);

        // 8Hz tremolo
        const tremolo = ctx.createOscillator();
        tremolo.type = "sine";
        tremolo.frequency.value = 8;
        const tremoloGain = ctx.createGain();
        tremoloGain.gain.value = 0.015; // half the base gain for AM depth

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.setValueAtTime(0.03, now + 0.35);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);

        tremolo.connect(tremoloGain);
        tremoloGain.connect(env.gain);
        source.connect(bpf);
        bpf.connect(env);
        env.connect(sfxGain);
        source.start(now); tremolo.start(now);
        source.stop(now + 0.41); tremolo.stop(now + 0.41);
        this.sfxNodeCount += 2;

        source.addEventListener("ended", () => {
          source.disconnect(); bpf.disconnect(); env.disconnect();
          tremolo.disconnect(); tremoloGain.disconnect();
          this.sfxNodeCount -= 2;
        });
        break;
      }

      case "whinny": {
        // Like neigh but shorter and higher pitch — an excited horse
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(400 * pitch, now);
        osc.frequency.linearRampToValueAtTime(600 * pitch, now + 0.2);

        const env = ctx.createGain();
        env.gain.setValueAtTime(0.03, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);

        osc.connect(env);
        env.connect(sfxGain);
        osc.start(now);
        osc.stop(now + 0.21);
        this.sfxNodeCount++;

        osc.addEventListener("ended", () => {
          osc.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });
        break;
      }

      default:
        break;
    }
  }

  // ─── Creature Footsteps ────────────────────────────────────────────────────────

  /**
   * Play a creature footfall sound appropriate to the species and gait.
   */
  private _playCreatureStep(species: string, gait: string): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;

    switch (species) {

      case "cat":
      case "dog": {
        // Very soft — tiny padded feet, barely audible
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 3000, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.01, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        source.connect(hpf); hpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.02);
        this.sfxNodeCount++;
        source.addEventListener("ended", () => {
          source.disconnect(); hpf.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });
        break;
      }

      case "horse":
      case "pony": {
        // Percussive hoofbeats — gain and strike pattern vary by gait
        let baseGain: number;
        let strikes: number[];
        switch (gait) {
          case "trot":
            baseGain = 0.04;
            strikes = [0, 0.04]; // double strike, second at 40ms
            break;
          case "canter":
            baseGain = 0.045;
            strikes = [0, 0.03, 0.08]; // triple strike
            break;
          case "gallop":
            baseGain = 0.05;
            strikes = [0, 0.025, 0.05, 0.075]; // rapid quad strike
            break;
          case "walk":
          default:
            baseGain = 0.035;
            strikes = [0]; // single strike
            break;
        }

        strikes.forEach((offset, i) => {
          if (this.sfxNodeCount >= MAX_SFX_NODES) return;
          const t = now + offset;
          const vol = i === 0 ? baseGain : baseGain * 0.7; // subsequent strikes softer
          const source = this._whiteSource(ctx);
          const bpf = createBPF(ctx, 800, 6);
          const env = ctx.createGain();
          env.gain.setValueAtTime(0, t);
          env.gain.linearRampToValueAtTime(vol, t + 0.001); // sharp attack
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
          source.connect(bpf); bpf.connect(env); env.connect(sfxGain);
          source.start(t); source.stop(t + 0.035);
          this.sfxNodeCount++;
          source.addEventListener("ended", () => {
            source.disconnect(); bpf.disconnect(); env.disconnect();
            this.sfxNodeCount--;
          });
        });
        break;
      }

      case "wolf": {
        // Like cat but slightly louder, with a low thud component
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 3000, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.015, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        source.connect(hpf); hpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.02);
        this.sfxNodeCount++;
        source.addEventListener("ended", () => {
          source.disconnect(); hpf.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });

        // Low thud component
        if (this.sfxNodeCount < MAX_SFX_NODES) {
          const thud = this._brownSource(ctx);
          const bpf = createBPF(ctx, 200, 2);
          const thudEnv = ctx.createGain();
          thudEnv.gain.setValueAtTime(0.01, now);
          thudEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
          thud.connect(bpf); bpf.connect(thudEnv); thudEnv.connect(sfxGain);
          thud.start(now); thud.stop(now + 0.025);
          this.sfxNodeCount++;
          thud.addEventListener("ended", () => {
            thud.disconnect(); bpf.disconnect(); thudEnv.disconnect();
            this.sfxNodeCount--;
          });
        }
        break;
      }

      case "dragon": {
        // Heavy thud — you feel it more than hear it
        const source = this._brownSource(ctx);
        const lpf = createLPF(ctx, 300, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.04, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        source.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.065);
        this.sfxNodeCount++;
        source.addEventListener("ended", () => {
          source.disconnect(); lpf.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });
        break;
      }

      default: {
        // Default: soft padded (same as cat)
        const source = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 3000, 0.7);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.01, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        source.connect(hpf); hpf.connect(env); env.connect(sfxGain);
        source.start(now); source.stop(now + 0.02);
        this.sfxNodeCount++;
        source.addEventListener("ended", () => {
          source.disconnect(); hpf.disconnect(); env.disconnect();
          this.sfxNodeCount--;
        });
        break;
      }
    }
  }

  // ─── Material Impact Sounds ──────────────────────────────────────────────────

  /**
   * Play a material-based impact sound.
   * Each material has a distinct physical character:
   *   wood   → warm hollow thock with mid-frequency resonance
   *   metal  → bright metallic ring with harmonic series
   *   stone  → heavy deep crack with subsonic thud
   *   glass  → bright crystalline clink with high shimmer
   *   fabric → muffled soft thump, barely there
   *   ceramic → hollow tap with bright overtone
   *   leather → soft creak with low body
   *   paper  → high rustle burst
   *   crystal → harmonic bell ring
   *   organic → dull low thud
   */
  private _playMaterialImpact(material: string, intensity: number): void {
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    if (!ctx || !sfxGain) return;
    if (this.sfxNodeCount >= MAX_SFX_NODES) return;

    const now = ctx.currentTime;
    const vol = 0.02 + intensity * 0.04;

    switch (material) {
      case "wood": {
        // Warm hollow thock — BPF noise at 400Hz + resonant sine at 180Hz
        const noise = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 400, 4);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
        noise.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.06);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // Resonant body
        const body = ctx.createOscillator();
        body.type = "sine"; body.frequency.value = 180;
        const bodyEnv = ctx.createGain();
        bodyEnv.gain.setValueAtTime(vol * 0.6, now);
        bodyEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        body.connect(bodyEnv); bodyEnv.connect(sfxGain);
        body.start(now); body.stop(now + 0.09);
        this.sfxNodeCount++;
        body.addEventListener("ended", () => { body.disconnect(); bodyEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "metal": {
        // Bright metallic ring — harmonic series at 1200/2400/4800Hz with long decay
        [1200, 2400, 4800].forEach((freq, i) => {
          if (this.sfxNodeCount >= MAX_SFX_NODES) return;
          const osc = ctx.createOscillator();
          osc.type = "sine"; osc.frequency.value = freq;
          const env = ctx.createGain();
          const v = vol * (1 - i * 0.25);
          env.gain.setValueAtTime(v, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.3 - i * 0.05);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 0.35);
          this.sfxNodeCount++;
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        });
        // Transient click
        const click = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 4000, 1.5);
        const clickEnv = ctx.createGain();
        clickEnv.gain.setValueAtTime(vol * 1.2, now);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);
        click.connect(hpf); hpf.connect(clickEnv); clickEnv.connect(sfxGain);
        click.start(now); click.stop(now + 0.02);
        this.sfxNodeCount++;
        click.addEventListener("ended", () => { click.disconnect(); hpf.disconnect(); clickEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "stone": {
        // Heavy deep crack — brown noise thud + low sine
        const noise = this._brownSource(ctx);
        const lpf = createLPF(ctx, 250, 0.8);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 1.3, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        noise.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        noise.start(now); noise.stop(now + 0.09);
        this.sfxNodeCount++;
        noise.addEventListener("ended", () => { noise.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // Sub thud
        const sub = ctx.createOscillator();
        sub.type = "sine"; sub.frequency.value = 65;
        const subEnv = ctx.createGain();
        subEnv.gain.setValueAtTime(vol * 0.8, now);
        subEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        sub.connect(subEnv); subEnv.connect(sfxGain);
        sub.start(now); sub.stop(now + 0.11);
        this.sfxNodeCount++;
        sub.addEventListener("ended", () => { sub.disconnect(); subEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "glass": {
        // Bright crystalline clink
        const osc = ctx.createOscillator();
        osc.type = "sine"; osc.frequency.value = 3200;
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.8, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
        osc.connect(env); env.connect(sfxGain);
        osc.start(now); osc.stop(now + 0.17);
        this.sfxNodeCount++;
        osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // High shimmer
        const shimmer = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 6000, 2);
        const shimEnv = ctx.createGain();
        shimEnv.gain.setValueAtTime(vol * 0.4, now);
        shimEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        shimmer.connect(hpf); hpf.connect(shimEnv); shimEnv.connect(sfxGain);
        shimmer.start(now); shimmer.stop(now + 0.09);
        this.sfxNodeCount++;
        shimmer.addEventListener("ended", () => { shimmer.disconnect(); hpf.disconnect(); shimEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "fabric": {
        // Soft muffled thump
        const src = this._brownSource(ctx);
        const lpf = createLPF(ctx, 200, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.4, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        src.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.05);
        this.sfxNodeCount++;
        src.addEventListener("ended", () => { src.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "ceramic": {
        // Hollow tap with bright overtone
        const tap = this._whiteSource(ctx);
        const bpf = createBPF(ctx, 1800, 6);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.7, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        tap.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        tap.start(now); tap.stop(now + 0.05);
        this.sfxNodeCount++;
        tap.addEventListener("ended", () => { tap.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        // Resonant ring
        const ring = ctx.createOscillator();
        ring.type = "sine"; ring.frequency.value = 2200;
        const ringEnv = ctx.createGain();
        ringEnv.gain.setValueAtTime(vol * 0.3, now);
        ringEnv.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        ring.connect(ringEnv); ringEnv.connect(sfxGain);
        ring.start(now); ring.stop(now + 0.12);
        this.sfxNodeCount++;
        ring.addEventListener("ended", () => { ring.disconnect(); ringEnv.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "leather": {
        // Soft creak
        const src = this._brownSource(ctx);
        const bpf = createBPF(ctx, 600, 2);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.5, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        src.connect(bpf); bpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.07);
        this.sfxNodeCount++;
        src.addEventListener("ended", () => { src.disconnect(); bpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "paper": {
        // High rustle
        const src = this._whiteSource(ctx);
        const hpf = createHPF(ctx, 3000, 0.8);
        const lpf = createLPF(ctx, 8000, 0.5);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.5, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
        src.connect(hpf); hpf.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.06);
        this.sfxNodeCount++;
        src.addEventListener("ended", () => { src.disconnect(); hpf.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
      case "crystal": {
        // Harmonic bell ring — two detuned high sines
        [2600, 3900].forEach((freq) => {
          if (this.sfxNodeCount >= MAX_SFX_NODES) return;
          const osc = ctx.createOscillator();
          osc.type = "sine"; osc.frequency.value = freq;
          const env = ctx.createGain();
          env.gain.setValueAtTime(vol * 0.6, now);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
          osc.connect(env); env.connect(sfxGain);
          osc.start(now); osc.stop(now + 0.22);
          this.sfxNodeCount++;
          osc.addEventListener("ended", () => { osc.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        });
        break;
      }
      case "organic":
      default: {
        // Dull low thud
        const src = this._brownSource(ctx);
        const lpf = createLPF(ctx, 300, 0.6);
        const env = ctx.createGain();
        env.gain.setValueAtTime(vol * 0.5, now);
        env.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
        src.connect(lpf); lpf.connect(env); env.connect(sfxGain);
        src.start(now); src.stop(now + 0.06);
        this.sfxNodeCount++;
        src.addEventListener("ended", () => { src.disconnect(); lpf.disconnect(); env.disconnect(); this.sfxNodeCount--; });
        break;
      }
    }
  }

  // ─── Shared Noise Source Helpers ─────────────────────────────────────────────

  /**
   * Create a brown noise AudioBufferSourceNode from the shared buffer.
   * Avoids re-generating the buffer each time.
   */
  private _brownSource(ctx: AudioContext): AudioBufferSourceNode {
    if (!this._brownBuffer) {
      const temp = createNoise(ctx, "brown");
      this._brownBuffer = temp.buffer;
    }
    const source = ctx.createBufferSource();
    source.buffer = this._brownBuffer;
    source.loop = true;
    return source;
  }

  /**
   * Create a white noise AudioBufferSourceNode from the shared buffer.
   */
  private _whiteSource(ctx: AudioContext): AudioBufferSourceNode {
    if (!this._whiteBuffer) {
      const temp = createNoise(ctx, "white");
      this._whiteBuffer = temp.buffer;
    }
    const source = ctx.createBufferSource();
    source.buffer = this._whiteBuffer;
    source.loop = true;
    return source;
  }

  /**
   * Create a pink noise AudioBufferSourceNode from the shared buffer.
   */
  private _pinkSource(ctx: AudioContext): AudioBufferSourceNode {
    if (!this._pinkBuffer) {
      const temp = createNoise(ctx, "pink");
      this._pinkBuffer = temp.buffer;
    }
    const source = ctx.createBufferSource();
    source.buffer = this._pinkBuffer;
    source.loop = true;
    return source;
  }

  // ─── Cleanup ──────────────────────────────────────────────────────────────────

  /**
   * Fully tear down the audio engine: dispose all layers, close the context,
   * remove event listeners, release buffers. After calling destroy(), the
   * engine is inert and resume() will create a fresh context.
   */
  destroy(): void {
    // Dispose all ambient layers
    for (const [, layer] of this.activeAmbient) {
      try { layer.dispose(); } catch { /* already stopped */ }
    }
    this.activeAmbient.clear();

    // Remove visibility listener
    if (this._boundVisHandler) {
      document.removeEventListener("visibilitychange", this._boundVisHandler);
      this._boundVisHandler = null;
    }

    // Close the AudioContext
    if (this.ctx && this.ctx.state !== "closed") {
      void this.ctx.close();
    }

    // Release all references
    this.ctx = null;
    this.masterGain = null;
    this.ambientGain = null;
    this.sfxGain = null;
    this.reverb = null;
    this.compressor = null;
    this._brownBuffer = null;
    this._whiteBuffer = null;
    this._pinkBuffer = null;
    this.ambientNodeCount = 0;
    this.sfxNodeCount = 0;
    this._titleAmbientActive = false;
    this._lastAmbientVolume = -1;
  }
}
